/**
 * Chat store — leaf module (Perf 3.1.b Step 4). The largest store: chat list,
 * messages, send/receive, title sync. Socket via getSocket(); sibling stores
 * via getStores() (runtime, to avoid static barrel/circular import).
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Platform } from 'react-native';
import getRelativeTime from '@/constants/utils/timestamp';
import { IN_FLIGHT_TURN_MERGE_GUARD_MS } from '@/constants/config';
import { mapSerializedMessageToMessage, messageHasImageOutput, messageHasMediaOutput, pickAuthoritativeInferences, pickAuthoritativeMessageText } from '@/constants/utils/message';
import { shouldPreserveExistingMessageFiles, stripWorkInjectedPrompt } from '@/constants/work';
import { getTranslation } from '@/hooks/useTranslation';
import { trackActivationOnce, ACTIVATION_ACTION } from '@/constants/utils/analyticsEvents';
import { chatsApi, inferencesApi } from '@/lib/api/chats';
import { collectionsApi } from '@/lib/api/collections';
import { getStorage } from './storage';
import { FILTER_CONFIGS } from './filterConfigs';
import { asEntityId, pinnedIdSetFrom, sameEntityId } from './entityId';
import { mergeStreamingDraftMessages } from '@/lib/streamingDraft';
import {
  collectOwnedChatIds,
  filterChatsAgainstExistingIds,
  filterChatsAgainstListingWindow,
  EXISTING_CHAT_IDS_BATCH,
} from '@/lib/reconcileDeletedChats';

const getSocket = () => require('@/lib/socket').default;
const getStores = () => require('@/store');

// Sanitize chat titles that may arrive as JSON objects or JSON strings from the LLM
function isPlaceholderChatTitle(title) {
  if (!title || typeof title !== 'string') return true;
  const normalized = title.trim().toLowerCase();
  return normalized === 'untitled chat'
    || normalized === 'new chat'
    || normalized === 'untitled conversation'
    || normalized === 'voice conversation'
    || normalized === 'voice chat'
    || normalized === 'untitled work'
    || normalized === 'coworker thread';
}

function sanitizeChatTitle(title) {
  if (!title) return 'Untitled Chat';
  if (typeof title === 'string') {
    const trimmed = title.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed.title && typeof parsed.title === 'string') return parsed.title.trim();
      } catch (e) { /* not JSON, use as-is */ }
    }
    return title;
  }
  if (typeof title === 'object' && title !== null) {
    if (title.title && typeof title.title === 'string') return title.title.trim();
    const stringVal = Object.values(title).find(v => typeof v === 'string');
    if (stringVal) return stringVal.trim();
    return 'Untitled Chat';
  }
  return String(title);
}

// Add this helper function at the top level of the chat store
const sortChatsWithPinned = (chats, pinnedIds) => {
  const pinnedSet = pinnedIdSetFrom(pinnedIds);
  return [...chats].sort((a, b) => {
    const aIsPinned = pinnedSet.has(asEntityId(a.id));
    const bIsPinned = pinnedSet.has(asEntityId(b.id));
    
    // First sort by pinned status
    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;
    
    // For items with same pinned status, maintain their relative order
    // by comparing their timestamps (assuming newer items should be first)
    const aTime = new Date(a.updatedAt || a.createdAt).getTime();
    const bTime = new Date(b.updatedAt || b.createdAt).getTime();
    return bTime - aTime;
  });
};

// A chat that was created as a hidden backing conversation for a Tool run
// must NEVER appear in the user's chat list unless the user explicitly
// promoted it via "Continue in chat". The backend filters this on every
// list endpoint, but we duplicate the rule client-side because:
//   1. The chat store is persisted to localStorage. Conversations cached
//      from before this filter existed would otherwise live on forever.
//   2. We process chats from many sources (sidebar bootstrap, /conversations
//      pagination, search results, socket pushes, cross-tab rehydrate, new-
//      conversation creation). One missed call site = leaked rows.
// See backend/models/Conversation.js (`flags.isToolPromotedToChat`) and
// docs/TOOLS.md → "Continue in chat".
// Exported below via the store's `isHiddenToolRunChat` action so UI components
// (sidebar, library page, search modal, etc.) can apply the same predicate as
// a final render-time gate. Defense-in-depth: even if stale chats slip into
// `state.chats` from any source — persisted localStorage from before the
// filter existed, a code path that bypasses `addChats`, an out-of-date socket
// payload — the UI layer never renders them.
const isHiddenToolRunChat = (chat) => {
  if (!chat) return false;
  const flags = chat.flags || {};
  if (flags.isToolPromotedToChat) return false;
  if (flags.isToolRun === true) return true;
  // Belt-and-braces: some legacy data shapes can be missing `flags.isToolRun`
  // entirely even though the conversation is clearly a tool-run backing
  // record. If we have a `tool` reference set we can treat it as one too.
  // The backend serializer strips this for sidebar payloads, so it only
  // catches detail responses, but having it here is essentially free.
  if (chat.tool && (chat.tool._id || typeof chat.tool === 'string')) return true;
  return false;
};

const useChatStore = create(
  persist(
  (set, get) => ({
  _hasHydrated: false,
  _fetchChatsPromise: null,
  _reconcileDeletedChatsPromise: null,
  _lastDeletedChatPruneAt: 0,
  chats: [],
  deletingChatId: null,
  // Multi-select mode for chats
  isSelectingMultipleChats: false,
  selectedChatIds: [],
  filters: {
    type: 'all',
    sort: null,
    inCollectionId: null,
    withoutCollections: false
  },
  searchQuery: '',
  searchResults: [],
  // Plus win-back: opaque placeholder rows returned by /json/conversations/search
  // for churned-Plus users (matches that hit conversations now locked behind
  // the paywall). Shape: { id, isPlusLocked: true, updatedAt, createdAt }.
  searchLockedResults: [],
  // Plus win-back state, populated from /json/conversations (default sidebar)
  // and /json/conversations/search responses. Null/undefined for never-Plus
  // and current-Plus users. See backend/utils/plus_lock.js.
  //   { isChurnedPlus, visibleCap, visibleCount, hiddenCount,
  //     lastAsPlus: { id, title, updatedAt } | null, canceledPlusAt }
  plusLock: null,
  searchPlusLock: null,
  selectedCitation: null,

  // Follow-up suggestions per conversation: { [conversationId]: { messageId, suggestions, timestamp } }
  followUpSuggestions: {},

  // "Move this chat to <collection>?" suggestions per conversation:
  // { [conversationId]: { collectionId, collectionTitle, timestamp } }
  // Seeded from the socket event; persisted pending suggestions arrive on the
  // serialized chat itself (chat.collectionSuggestion).
  collectionSuggestions: {},

  searchPagination: {
    current: 1,
    pages: 1,
    total: 0,
    perPage: 20,
    hasMore: false,
    filter: null,
    sort: null,
    inCollectionId: null,
    withoutCollections: false
  },
  isSearching: false,
  searchError: null,

  // Deep search (message content search modal)
  deepSearch: {
    query: '',
    results: [],
    mode: 'global',
    scopedConversationIds: [],
    scopedConversationTitles: {},
    pagination: { current: 1, pages: 1, total: 0, perPage: 20, hasMore: false },
    isSearching: false,
    error: null,
  },

  pagination: {
    current: 1,
    pages: 1,
    total: 0,
    perPage: 20,
    hasMore: false,
    filter: null,
    sort: null,
    inCollectionId: null,
    withoutCollections: false
  },

  setPagination: (pagination) => set({ pagination }),
  setDeletingChatId: (id) => set({ deletingChatId: id }),
  clearDeletingChatId: () => set({ deletingChatId: null }),

  // Short-lived tombstones so a late `conversation_created` socket event
  // cannot resurrect a temp chat after navigate-away cleanup on native.
  // Not persisted — see partialize.
  _recentlyDeletedTemporaryChatIds: {},
  markTemporaryChatDeleted: (id) => {
    if (!id) return;
    const now = Date.now();
    set((state) => {
      const next = { ...state._recentlyDeletedTemporaryChatIds, [id]: now };
      for (const [chatId, ts] of Object.entries(next)) {
        if (now - ts > 60_000) delete next[chatId];
      }
      return { _recentlyDeletedTemporaryChatIds: next };
    });
  },
  wasTemporaryChatRecentlyDeleted: (id) => {
    if (!id) return false;
    const ts = get()._recentlyDeletedTemporaryChatIds?.[id];
    if (!ts) return false;
    if (Date.now() - ts > 60_000) return false;
    return true;
  },
  
  setFilters: (filters) => set({ filters }),

  // Follow-up suggestions management
  setFollowUpSuggestions: (conversationId, data) => set(state => {
    const existing = state.followUpSuggestions[conversationId];
    const existingTimestamp = existing?.timestamp ? new Date(existing.timestamp).getTime() : 0;
    const nextTimestamp = data?.timestamp ? new Date(data.timestamp).getTime() : Date.now();
    if (existingTimestamp > nextTimestamp) {
      return state;
    }
    return {
      followUpSuggestions: {
        ...state.followUpSuggestions,
        [conversationId]: {
          ...data,
          timestamp: nextTimestamp,
        }
      }
    };
  }),
  
  clearFollowUpSuggestions: (conversationId) => set(state => {
    if (conversationId) {
      const { [conversationId]: _, ...rest } = state.followUpSuggestions;
      return { followUpSuggestions: rest };
    }
    return { followUpSuggestions: {} };
  }),
  
  getFollowUpSuggestions: (conversationId) => {
    return get().followUpSuggestions[conversationId] || null;
  },

  // Collection suggestion management
  setCollectionSuggestion: (conversationId, data) => set(state => ({
    collectionSuggestions: {
      ...state.collectionSuggestions,
      [conversationId]: data,
    }
  })),

  clearCollectionSuggestion: (conversationId) => set(state => {
    if (!conversationId) return { collectionSuggestions: {} };
    const { [conversationId]: _, ...rest } = state.collectionSuggestions;
    return { collectionSuggestions: rest };
  }),

  // Multi-select controls
  startSelectingMultipleChats: () => set({ isSelectingMultipleChats: true, selectedChatIds: [] }),
  stopSelectingMultipleChats: () => set({ isSelectingMultipleChats: false, selectedChatIds: [] }),
  toggleSelectingMultipleChats: () => set(state => ({
    isSelectingMultipleChats: !state.isSelectingMultipleChats,
    selectedChatIds: !state.isSelectingMultipleChats ? [] : state.selectedChatIds
  })),
  clearSelectedChats: () => set({ selectedChatIds: [] }),
  selectChatForMulti: (id) => set(state => {
    const alreadySelected = state.selectedChatIds.includes(id);
    if (alreadySelected) return { selectedChatIds: state.selectedChatIds };
    if (state.selectedChatIds.length >= 20) {
      try {
        getStores().useToastStore.getState().actions.addToast(getTranslation('store.selectLimit'), 'warning');
      } catch (_) {}
      return { selectedChatIds: state.selectedChatIds };
    }
    return { selectedChatIds: [...state.selectedChatIds, id] };
  }),
  unselectChatForMulti: (id) => set(state => ({
    selectedChatIds: state.selectedChatIds.filter(cid => cid !== id)
  })),
  toggleSelectChatForMulti: (id) => set(state => {
    const isSelected = state.selectedChatIds.includes(id);
    if (isSelected) {
      return { selectedChatIds: state.selectedChatIds.filter(cid => cid !== id) };
    }
    if (state.selectedChatIds.length >= 20) {
      try {
        getStores().useToastStore.getState().actions.addToast(getTranslation('store.selectLimit'), 'warning');
      } catch (_) {}
      return { selectedChatIds: state.selectedChatIds };
    }
    return { selectedChatIds: [...state.selectedChatIds, id] };
  }),

  handleFilterSortClick: async (labelId, router) => {
    const currentFilters = get().filters;
    let nextFilters = { ...currentFilters };

    if (!labelId) {
      nextFilters = { ...currentFilters, type: 'all', sort: null };
    } else {
      const label = FILTER_CONFIGS.library.filters
        .concat(FILTER_CONFIGS.library.sort)
        .find(l => l.id === labelId);

      if (!label) return;

      if (label.type === 'filter') {
        nextFilters = { ...currentFilters, type: label.id };
      } else if (label.type === 'sort') {
        if (label.id === 'archived' || label.id === 'shared') {
          nextFilters = { ...currentFilters, type: label.id };
        } else {
          nextFilters = { ...currentFilters, sort: label.id };
        }
      }
    }

    set({ filters: nextFilters });

    try {
      const chats = await get().fetchChats(1);
      if (!router) return;

      let currentConversationId = null;
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        currentConversationId = currentPath.match(/\/c\/([^\/]+)/)?.[1];
      } else {
        currentConversationId = getStores().useRouteStore.getState().currentLibraryId;
      }

      if (!currentConversationId) return;

      const chatExists = chats.some(chat => chat.id === currentConversationId);
      if (chatExists) return;

      if (chats.length > 0) {
        getStores().useRouteStore.getState().actions.setCurrentLibraryId(chats[0].id);
        router.replace(`/c/${chats[0].id}`);
      } else {
        router.replace('/app');
      }
    } catch (error) {
      console.error('Error applying chat filters:', error);
    }
  },

  retryTitleRetrieval: async (userId, conversationId, maxRetries = 3) => {
    // We only ever need ONE outstanding emit per conversation — the backend
    // is idempotent and will skip regeneration once a real title is saved.
    // What we DO need is a generous per-attempt timeout because admins can
    // configure thinking/reasoning models (Gemini 3 Pro, GPT-5 thinking, etc.)
    // for the title generator, which routinely take 15-30s end-to-end.
    const PER_ATTEMPT_TIMEOUT_MS = 45000;

    let retryCount = 0;

    const attemptRetrieval = () => {
      return new Promise((resolve) => {
        if (!getSocket().socket) {
          resolve(false);
          return;
        }

        // Check if title already exists in store
        const existingChat = get().chats.find(c => c.id === conversationId);
        if (existingChat?.title && !isPlaceholderChatTitle(existingChat.title)) {
          resolve(true);
          return;
        }

        let settled = false;
        let timeoutHandle = null;

        const cleanup = () => {
          if (getSocket().socket) {
            getSocket().socket.off('conversation_title', titleListener);
          }
          if (timeoutHandle) clearTimeout(timeoutHandle);
        };

        const titleListener = (data) => {
          if (settled) return;
          const eventId = data.id || data.conversationId;
          if (eventId === conversationId && data.title) {
            const cleanTitle = sanitizeChatTitle(data.title);
            set(state => ({
              chats: state.chats.map(c =>
                c.id === conversationId ? { ...c, title: cleanTitle } : c
              )
            }));
            settled = true;
            cleanup();
            resolve(true);
          }
        };

        getSocket().socket.on('conversation_title', titleListener);
        getSocket().emitRetrieveConversationTitle(userId, conversationId);

        timeoutHandle = setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(false);
        }, PER_ATTEMPT_TIMEOUT_MS);
      });
    };

    while (retryCount < maxRetries) {
      const success = await attemptRetrieval();
      if (success) break;

      retryCount++;
      if (retryCount < maxRetries) {
        // Backoff between fresh emit attempts (slow models, dropped socket).
        const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  },

  addChat: (chat) => {
    // Use addChats internally to ensure consistent processing
    return get().addChats([chat]);
  },
  updateLocalChatDetails: (conversationId, updates) => {
    let updatedChat = null;

    // IMPORTANT: Compute the updated chat INSIDE the set() callback to ensure
    // we always read the latest state. Reading with get() outside set() can
    // return a stale snapshot if another set() was batched just before this one,
    // which in fast-mode's rapid event flow can cause newly-added user messages
    // to be silently dropped (the stale chat object overwrites the fresh one).
    set(state => {
      const chat = state.chats.find(c => c.id === conversationId);
      if (!chat) return state;

      // Skip the update when all values are already identical to avoid
      // creating a new chats array reference that triggers re-renders.
      const updateKeys = Object.keys(updates);
      const hasChange = updateKeys.some(key => chat[key] !== updates[key]);
      if (!hasChange) {
        updatedChat = chat;
        return state;
      }

      updatedChat = { ...chat, ...updates };

      return {
        chats: state.chats.map(c =>
          c.id === conversationId ? updatedChat : c
        )
      };
    });

    return updatedChat;
  },
  // Flip a conversation into the "thinking/waiting" state so ChatView renders
  // the inferencing indicator. Called from two places:
  //   1. c/[id].jsx after loading a conversation whose `flags.isInferencing`
  //      is still true (e.g. a prompt run the user navigated straight into).
  //   2. socket.js `conversation_stream_start` when the live event arrives for
  //      a conversation not yet in the store (the prompt/tool navigation race).
  // For case 2 the chat may be missing, so we fetch it first. addChats knows to
  // drop hidden tool-run backing threads, so this can't fabricate a phantom
  // sidebar row.
  beginInferencingConversation: (conversationId) => {
    if (!conversationId) return;

    const applyInferencingFlags = () => {
      const chatStore = useChatStore.getState();
      if (!chatStore.chats.find(c => c.id === conversationId)) return;
      chatStore.updateLocalChatDetails(conversationId, {
        isInferencing: true,
        isInferencingWaiting: true,
        pendingInferenceStatus: undefined,
        pendingInferenceStage: undefined,
        pendingCanFastAnswer: undefined,
        pendingInferenceStatusTimestamp: undefined,
      });
    };

    if (get().chats.find(c => c.id === conversationId)) {
      applyInferencingFlags();
      return;
    }

    setTimeout(async () => {
      try {
        const data = await chatsApi.getConversationById(conversationId);
        if (data) {
          useChatStore.getState().addChats([data]);
          applyInferencingFlags();
        }
      } catch (err) {
        console.warn('[Store] beginInferencingConversation: failed to fetch conversation', conversationId, err?.message || err);
      }
    }, 0);
  },
  updateChatDetails: async (conversationId, updates) => {
    try {
      const updatedChat = await chatsApi.updateChat(conversationId, updates);
      
      // Only update the specific metadata fields we requested — NOT the entire
      // conversation object.  The API response includes `messages` with raw /
      // un-normalised data that would overwrite the locally-processed messages
      // (which went through addChats → mapSerializedMessageToMessage).  A
      // shallow spread of the full response was corrupting file metadata
      // (e.g. missing sizeInBytes → "NaN MB") and potentially other enriched
      // message fields.
      const safeFields = ['title', 'visibility', 'customControls', 'updatedAt', 'flags'];
      const safeUpdates = {};
      for (const key of safeFields) {
        if (key in updatedChat) {
          safeUpdates[key] = updatedChat[key];
        }
      }

      set(state => ({
        chats: state.chats.map(c => 
          c.id === conversationId ? { ...c, ...safeUpdates } : c
        )
      }));

      return updatedChat;
    } catch (error) {
      console.error('Error updating chat:', error);
      throw error;
    }
  },
  renameChat: async (id, newTitle) => {
    try {
      await get().updateChatDetails(id, { title: newTitle });
    } catch (error) {
      console.error('Error renaming chat:', error);
      throw error;
    }
  },
  setChatVisibility: async (id, isVisible) => {
    try {
      await get().updateChatDetails(id, { visibility: isVisible ? 'public' : 'private' });
    } catch (error) {
      console.error('Error setting chat visibility:', error);
      throw error;
    }
  },
  archiveChat: (id) => set(state => ({
    chats: state.chats.filter(c => c.id !== id)
  })),
  unarchiveChat: (id) => set(state => ({
    chats: state.chats.map(c => 
      c.id === id ? { ...c, isArchived: false } : c
    )
  })),
  // Multi-archive (stubbed: no API yet)
  archiveSelectedChats: async () => {
    const { selectedChatIds } = get();
    if (!Array.isArray(selectedChatIds) || selectedChatIds.length === 0) return;
    const prevChats = get().chats;
    const prevSelectedChatIds = [...selectedChatIds];
    const prevIsSelectingMultipleChats = get().isSelectingMultipleChats;
    const idsToArchive = [...selectedChatIds];
    // Optimistic update: remove archived chats from current list locally
    set(state => ({
      chats: state.chats.filter(c => !idsToArchive.includes(c.id)),
      selectedChatIds: [],
      isSelectingMultipleChats: false,
    }));
    try {
      await chatsApi.bulkArchiveChats(idsToArchive);
    } catch (error) {
      console.error('Error bulk archiving conversations:', error);
      // Roll back optimistic update on failure
      set({
        chats: prevChats,
        selectedChatIds: prevSelectedChatIds,
        isSelectingMultipleChats: prevIsSelectingMultipleChats,
      });
    }
  },
  unarchiveSelectedChats: async () => {
    const { selectedChatIds } = get();
    if (!Array.isArray(selectedChatIds) || selectedChatIds.length === 0) return;
    const prevChats = get().chats;
    const prevSelectedChatIds = [...selectedChatIds];
    const prevIsSelectingMultipleChats = get().isSelectingMultipleChats;
    const idsToUnarchive = [...selectedChatIds];
    // Optimistic update: remove unarchived chats from the archived filter view.
    set(state => ({
      chats: state.chats.filter(c => !idsToUnarchive.includes(c.id)),
      selectedChatIds: [],
      isSelectingMultipleChats: false,
    }));
    try {
      await chatsApi.bulkUnarchiveChats(idsToUnarchive);
    } catch (error) {
      console.error('Error bulk unarchiving conversations:', error);
      // Roll back optimistic update on failure
      set({
        chats: prevChats,
        selectedChatIds: prevSelectedChatIds,
        isSelectingMultipleChats: prevIsSelectingMultipleChats,
      });
    }
  },
  archiveAllChats: () => set(state => ({
    chats: state.chats.map(chat => ({ ...chat, isArchived: true }))
  })),
  unarchiveAllChats: () => set(state => ({
    chats: state.chats.map(chat => ({ ...chat, isArchived: false }))
  })),
  deleteChat: (id) => {
    set(state => ({ chats: state.chats.filter(c => c.id !== id) }));
    // Strip the deleted chat from every collection's `conversationIds` so the
    // client's in-memory collection state matches the backend's cascade-pull
    // (DELETE /json/conversations/:id pulls the id from any owning collection).
    // Without this, the id lingers in conversationIds until the next
    // bootstrap/detail fetch, which could re-introduce a stale count or let a
    // stale multi-select id survive scopeSelectionToCollection.
    //
    // Collections live in `useCollectionStore`, NOT on the chat store, so this
    // must read/write that store directly — reading `state.collections` off the
    // chat store yields `undefined` and `Object.values(undefined)` throws.
    try {
      const collections = getStores().useCollectionStore.getState().collections || {};
      let changed = false;
      const nextCollections = Object.values(collections).reduce((acc, col) => {
        if (col?.conversationIds?.length && col.conversationIds.includes(id)) {
          acc[col.id] = { ...col, conversationIds: col.conversationIds.filter(cid => cid !== id) };
          changed = true;
        } else {
          acc[col.id] = col;
        }
        return acc;
      }, {});
      if (changed) getStores().useCollectionStore.setState({ collections: nextCollections });
    } catch (_) { /* collection sync is best-effort; never block the delete */ }
  },
  // Bulk-add selected chats to a collection. The backend pulls the chats out
  // of any other collection they currently belong to, so this is a move when
  // the chats already live in a collection. `chatIds` lets callers pass an
  // explicit set (e.g. after scoping to the current collection page).
  addSelectedChatsToCollection: async (collectionId, chatIds) => {
    const selectedChatIds = Array.isArray(chatIds) && chatIds.length > 0
      ? [...chatIds]
      : [...(get().selectedChatIds || [])];
    if (!collectionId || selectedChatIds.length === 0) return { successCount: 0, failedIds: [] };
    const idsToAdd = selectedChatIds;
    const prevChats = get().chats;
    const prevSelectedChatIds = [...selectedChatIds];
    const prevIsSelectingMultipleChats = get().isSelectingMultipleChats;
    const prevCollections = getStores().useCollectionStore.getState().collections;
    const prevCollectionIds = getStores().useCollectionStore.getState().collectionIds;
    // Optimistic: update chats locally and update collections store locally
    set(state => ({
      chats: state.chats.map(c => idsToAdd.includes(c.id) ? { ...c, inCollectionId: collectionId } : c),
      selectedChatIds: [],
      isSelectingMultipleChats: false,
    }));
    try {
      const addConvLocal = getStores().useCollectionStore.getState().addConversationToCollectionLocally;
      idsToAdd.forEach(cid => addConvLocal?.(collectionId, cid));
    } catch (_) {}
    try {
      const updatedCollection = await collectionsApi.bulkAddConversations(collectionId, idsToAdd);
      // Ensure collection store has the updated detailed collection
      try {
        getStores().useCollectionStore.getState().addCollection(updatedCollection);
      } catch (_) {}
    } catch (error) {
      console.error('Error bulk adding conversations to collection:', error);
      // Roll back optimistic updates
      set({
        chats: prevChats,
        selectedChatIds: prevSelectedChatIds,
        isSelectingMultipleChats: prevIsSelectingMultipleChats,
      });
      try {
        getStores().useCollectionStore.setState({
          collections: prevCollections,
          collectionIds: prevCollectionIds,
        });
      } catch (_) {}
      throw error;
    }
    return { successCount: idsToAdd.length, failedIds: [] };
  },
  // Bulk remove the currently selected chats from a collection.
  // There is no bulk-remove API endpoint, so we fan out per-conversation
  // delete-from-collection requests and reconcile the optimistic state at the
  // end. Mirrors the optimistic-with-rollback pattern used by
  // archiveSelectedChats / addSelectedChatsToCollection.
  removeSelectedChatsFromCollection: async (collectionId) => {
    const { selectedChatIds } = get();
    if (!collectionId || !Array.isArray(selectedChatIds) || selectedChatIds.length === 0) return;
    const idsToRemove = [...selectedChatIds];
    const prevChats = get().chats;
    const prevSelectedChatIds = [...selectedChatIds];
    const prevIsSelectingMultipleChats = get().isSelectingMultipleChats;
    const collectionStore = getStores().useCollectionStore.getState();
    const prevCollections = collectionStore.collections;
    const prevCollectionIds = collectionStore.collectionIds;

    // Optimistic local update: drop these chats from the target collection's
    // conversationIds and clear their inCollectionId in the chat store.
    const targetCollection = prevCollections[collectionId];
    if (targetCollection) {
      const updatedCollection = {
        ...targetCollection,
        conversationIds: (targetCollection.conversationIds || []).filter(
          cid => !idsToRemove.includes(cid),
        ),
      };
      try {
        getStores().useCollectionStore.getState().addCollection(updatedCollection);
      } catch (_) {}
    }
    set(state => ({
      chats: state.chats.map(c =>
        idsToRemove.includes(c.id) ? { ...c, inCollectionId: null } : c,
      ),
      selectedChatIds: [],
      isSelectingMultipleChats: false,
    }));

    let failedIds = [];
    try {
      const results = await Promise.allSettled(
        idsToRemove.map(cid => collectionsApi.removeConversation(collectionId, cid)),
      );
      failedIds = results
        .map((r, idx) => (r.status === 'rejected' ? idsToRemove[idx] : null))
        .filter(Boolean);

      // Pick the freshest collection payload from any successful response so
      // we stay in sync with the server's authoritative conversationIds.
      const lastSuccess = [...results].reverse().find(r => r.status === 'fulfilled');
      if (lastSuccess?.value) {
        try {
          getStores().useCollectionStore.getState().addCollection(lastSuccess.value);
        } catch (_) {}
      }

      if (failedIds.length === idsToRemove.length) {
        throw new Error('All remove-from-collection requests failed');
      }
    } catch (error) {
      console.error('Error bulk removing conversations from collection:', error);
      // Roll back optimistic updates only if everything failed.
      set({
        chats: prevChats,
        selectedChatIds: prevSelectedChatIds,
        isSelectingMultipleChats: prevIsSelectingMultipleChats,
      });
      try {
        getStores().useCollectionStore.setState({
          collections: prevCollections,
          collectionIds: prevCollectionIds,
        });
      } catch (_) {}
      throw error;
    }

    return { successCount: idsToRemove.length - failedIds.length, failedIds };
  },
  // Multi-delete (stubbed: no API yet)
  deleteSelectedChats: async () => {
    const { selectedChatIds } = get();
    if (!Array.isArray(selectedChatIds) || selectedChatIds.length === 0) return;
    const prevChats = get().chats;
    const prevSelectedChatIds = [...selectedChatIds];
    const prevIsSelectingMultipleChats = get().isSelectingMultipleChats;
    const prevCollections = getStores().useCollectionStore.getState().collections;
    const idsToDelete = [...selectedChatIds];
    const deleteSet = new Set(idsToDelete);
    // Optimistic update: remove selected chats locally AND pull their ids out
    // of every collection's `conversationIds`, mirroring the backend's
    // bulk-delete cascade ($pull from conversationIds). Without this, deleted
    // ids linger in the client's collection state until the next bootstrap.
    set(state => ({
      chats: state.chats.filter(c => !deleteSet.has(c.id)),
      selectedChatIds: [],
      isSelectingMultipleChats: false,
    }));
    // Collections live in `useCollectionStore`, not the chat store — prune there
    // directly. Reading `state.collections` off the chat store yields undefined
    // and `Object.values(undefined)` throws.
    try {
      const collections = getStores().useCollectionStore.getState().collections || {};
      let collectionsChanged = false;
      const nextCollections = Object.values(collections).reduce((acc, col) => {
        if (col?.conversationIds?.length && col.conversationIds.some(cid => deleteSet.has(cid))) {
          acc[col.id] = { ...col, conversationIds: col.conversationIds.filter(cid => !deleteSet.has(cid)) };
          collectionsChanged = true;
        } else {
          acc[col.id] = col;
        }
        return acc;
      }, {});
      if (collectionsChanged) getStores().useCollectionStore.setState({ collections: nextCollections });
    } catch (_) { /* collection sync is best-effort; never block the delete */ }
    try {
      await chatsApi.bulkDeleteChats(idsToDelete);
    } catch (error) {
      console.error('Error bulk deleting conversations:', error);
      // Roll back optimistic update on failure
      set({
        chats: prevChats,
        selectedChatIds: prevSelectedChatIds,
        isSelectingMultipleChats: prevIsSelectingMultipleChats,
      });
      try {
        getStores().useCollectionStore.setState({ collections: prevCollections });
      } catch (_) {}
    }
  },
  setChatPinned: (id, isPinned) => set(state => {
    const currentUser = getStores().useAuthStore.getState().user;
    const targetId = asEntityId(id);
    const authPinnedIds = [...new Set((currentUser.pinnedConversations || []).map(asEntityId).filter(Boolean))];
    // Prefer the explicit next state for this id; sync other chats from auth.
    const pinnedIds = isPinned
      ? (authPinnedIds.includes(targetId) ? authPinnedIds : [...authPinnedIds, targetId])
      : authPinnedIds.filter(pId => pId !== targetId);

    const pinnedSet = new Set(pinnedIds);
    return {
      chats: sortChatsWithPinned(
        state.chats.map(c => ({
          ...c,
          isPinned: asEntityId(c.id) === targetId ? !!isPinned : pinnedSet.has(asEntityId(c.id)),
        })),
        pinnedIds,
      ),
    };
  }),

  // Backward-compatible toggle — derive next state from the chat row itself
  // (not auth), so callers that haven't migrated to setChatPinned stay correct
  // even if auth was already updated.
  togglePinChat: (id) => {
    const existing = get().chats.find(c => c.id === id);
    const nextPinned = !(existing?.isPinned);
    return get().setChatPinned(id, nextPinned);
  },

  addMessage: (conversationId, message) => {
    console.log('[Store] addMessage called:', { conversationId, messageRole: message.role, messageContent: message.content?.substring(0, 50) });
    
    return set(state => {
      // Check if the chat is currently active
      // We access the route store state directly since it's defined in the same file/module scope
      const currentChatId = getStores().useRouteStore.getState().currentLibraryId;
      const isChatActive = currentChatId === conversationId;

      // Only generate temporary ID if message.id is explicitly null/undefined
      // This allows updateLocalChatMessage to find messages that need to be updated
      const messageId = message.id !== null && message.id !== undefined 
        ? message.id 
        : 'msg_' + Math.random().toString(36).substring(2) + Date.now().toString(36);

      const newMessage = {
        ...message, // Spread first
        id: messageId, // Then set id to ensure it's never null
        // _stableKey: A key that never changes for the lifetime of this message.
        // When new_user_message arrives the `id` is swapped from a temporary
        // `msg_*` value to the real server ID.  FlashList uses the key returned
        // by keyExtractor to track items; changing it makes FlashList treat the
        // item as removed-then-added, which on iOS with
        // maintainVisibleContentPosition can cause the "new" item to never
        // render — making user messages disappear in fast-mode.
        _stableKey: message._stableKey || messageId,
        content: message.content,
        role: message.role === 'assistant' ? 'assistant' : 'user',
        timestamp: message.timestamp || message.date || new Date().toISOString(),
      };

      // Check if the chat exists
      const existingChat = state.chats.find(c => c.id === conversationId);

      if (existingChat) {
        // Check for duplicate message by ID (prevents double-adding from socket reconnection)
        const existingMessage = existingChat.messages?.find(m => {
          if (m.id === messageId) return true;
          if (m._id !== messageId) return false;
          if (typeof messageId === 'string' && messageId.endsWith('-streaming') && m.id && m.id !== messageId) {
            return false;
          }
          return true;
        });
        if (existingMessage) {
          return state; // Return unchanged state to prevent duplicate
        }
        
        // Also check for duplicate user messages by content+role within the last 60 seconds.
        // This is a safety net that prevents voice mode duplicates where the same
        // message can arrive with different IDs from voice socket vs backend.
        // Do not apply it to assistant messages: two models can legitimately
        // produce identical short answers in the same multi-model turn.
        // Keep this scoped to voice messages; normal chat must allow the same
        // text to be sent repeatedly as distinct turns.
        const isVoiceMessage = !!(
          newMessage.isVoiceMessage ||
          newMessage?.parameters?.source === 'voice'
        );
        if (isVoiceMessage && newMessage.role === 'user' && newMessage.content && newMessage.content.trim()) {
          const sixtySecondsAgo = Date.now() - 60000;
          const contentDuplicate = existingChat.messages?.find(m =>
            m.role === newMessage.role && 
            m.content === newMessage.content &&
            m.timestamp && new Date(m.timestamp).getTime() > sixtySecondsAgo
          );
          if (contentDuplicate) {
            return state; // Return unchanged state to prevent duplicate
          }
        }
        
        // Chat exists, add message to it
        return {
          chats: state.chats.map(c => 
            c.id === conversationId 
              ? { 
                  ...c, 
                  unreadCount: 0, // reset, done in backend too
                  lastReadAt: new Date(), // reset, done in backend too
                  messages: [...(c.messages || []), newMessage]
                }
              : c
          )
        };
      } else {
        // Chat isn't hydrated yet (navigation race, slow detail fetch, etc.).
        //
        // USER sends: show an optimistic shell immediately so the user's own
        // message never disappears while the real conversation is fetched.
        // This is safe — users only send into conversations they're actively
        // in, never into hidden tool-run backing conversations.
        //
        // ASSISTANT messages: do NOT fabricate a shell. These arrive for
        // hidden tool-run backing conversations (`flags.isToolRun`) that the
        // sidebar must never show — fabricating a row here is exactly the
        // old "Voice Conversation" placeholder leak. Fetch-and-reconcile via
        // addChats, which knows how to drop hidden tool-run chats.
        const isUserSend = newMessage.role === 'user';
        console.log('[Store] addMessage: Unknown conversationId', {
          conversationId,
          messageRole: message.role,
          optimisticShell: isUserSend,
        });

        setTimeout(async () => {
          try {
            const data = await chatsApi.getConversationById(conversationId);
            if (data) {
              useChatStore.getState().addChats([data]);
            }
          } catch (err) {
            console.warn('[Store] addMessage: Failed to fetch unknown conversation', conversationId, err?.message || err);
          }
        }, 0);

        if (!isUserSend) {
          return state;
        }

        const optimisticChat = {
          id: conversationId,
          title: 'Untitled Chat',
          messages: [newMessage],
          flags: {},
          unreadCount: 0,
          lastReadAt: new Date(),
          lastMessageTime: getRelativeTime(newMessage.timestamp),
          isInferencing: true,
          isInferencingWaiting: true,
        };

        return {
          chats: [...state.chats, optimisticChat],
        };
      }
    });
  },

  markChatAsRead: (conversationId) => {
    // Optimistic update
    set(state => ({
      chats: state.chats.map(c => 
        c.id === conversationId 
          ? { ...c, unreadCount: 0 }
          : c
      )
    }));
    
    // Call API in background
    chatsApi.markAsRead(conversationId).catch(err => {
      console.error('Failed to sync read status', err);
    });
  },

  updateLocalChatMessage: (conversationId, messageId, data = {}) => {
    const state = get();
    const chat = state.chats.find(c => c.id === conversationId);
    if (!chat) return null;

    let targetMessage;
    const chatMessages = chat.messages || [];
    if (messageId) {
      targetMessage = chatMessages.find((m) => {
        if (!(m.id === messageId || m._id === messageId)) return false;
        if (typeof messageId === 'string' && messageId.endsWith('-streaming') && m.id && m.id !== messageId) {
          return false;
        }
        return true;
      });
    } else {
      targetMessage = [...chatMessages].reverse().find(m => 
        m.role === 'user' && 
        (!m.id || (typeof m.id === 'string' && m.id.startsWith('msg_') && !m._id))
      );
    }
    if (Array.isArray(data?.files) && data?.files?.length > 0) {
      getStores().useFileStore.getState().actions.addFiles(data.files);
    }

    if (!targetMessage) return null;

    // Guard against a streaming/poll race wiping out a locally-held pending-jobs
    // inferences array with an empty/missing one from the server. Without this,
    // an "in progress" inference card can vanish mid-generation even though the
    // backend job is still pending.
    const mergedInferences = ('inferences' in data)
      ? pickAuthoritativeInferences(targetMessage.inferences, data.inferences)
      : undefined;
    const mergedContent = ('content' in data)
      ? (data?.flags?.isMemorySaveClaimStripped === true
        ? data.content
        : pickAuthoritativeMessageText(targetMessage.content, data.content))
      : undefined;
    const mergedThinkingContent = ('thinkingContent' in data)
      ? pickAuthoritativeMessageText(targetMessage.thinkingContent, data.thinkingContent)
      : undefined;
    let safeData = data;
    if (mergedInferences !== undefined && mergedInferences !== data.inferences) {
      safeData = { ...safeData, inferences: mergedInferences };
    }
    if (mergedContent !== undefined && mergedContent !== data.content) {
      safeData = { ...safeData, content: mergedContent };
    }
    if (mergedThinkingContent !== undefined && mergedThinkingContent !== data.thinkingContent) {
      safeData = { ...safeData, thinkingContent: mergedThinkingContent };
    }
    if (safeData.id && safeData.id !== targetMessage.id && !safeData._stableKey
      && typeof targetMessage._stableKey === 'string'
      && targetMessage._stableKey.endsWith('-streaming')) {
      safeData = { ...safeData, _stableKey: safeData.id };
    }

    const updatedMessage = { ...targetMessage, ...safeData };

    const targetId = targetMessage.id;
    const targetInternalId = targetMessage._id;

    // During streaming, only the assistant message content/flags change.
    // Skip the store update entirely when nothing meaningful changed to
    // avoid creating new array references that trigger FlatList re-diffs.
    const isStreamingUpdate = data.isStreaming || data.isTyping;
    if (isStreamingUpdate && targetMessage.content === data.content &&
        targetMessage.thinkingContent === data.thinkingContent &&
        targetMessage.isStreaming === data.isStreaming &&
        targetMessage.isThinking === data.isThinking &&
        targetMessage.isWaiting === data.isWaiting &&
        targetMessage.isTyping === data.isTyping) {
      return targetMessage;
    }

    set(state => {
      const chatIndex = state.chats.findIndex(c => c.id === conversationId);
      if (chatIndex === -1) return state;
      const chatEntry = state.chats[chatIndex];
      const currentMessages = chatEntry.messages || [];
      const matches = (msg) =>
        (targetId && msg.id === targetId) ||
        (targetInternalId && msg._id === targetInternalId) ||
        msg === targetMessage;
      let messageIndex = currentMessages.lastIndexOf(targetMessage);
      if (messageIndex === -1) messageIndex = currentMessages.findIndex(matches);
      if (messageIndex === -1) return state;

      const nextMessages = currentMessages.slice();
      nextMessages[messageIndex] = { ...currentMessages[messageIndex], ...safeData };
      for (let i = 0; i < nextMessages.length; i++) {
        if (i !== messageIndex && matches(nextMessages[i])) {
          nextMessages[i] = { ...nextMessages[i], ...safeData };
        }
      }
      const nextChats = state.chats.slice();
      nextChats[chatIndex] = { ...chatEntry, messages: nextMessages };
      return { chats: nextChats };
    });

    return updatedMessage;
  },

  // Poll a specific inference job and update the message state
  pollInferenceJob: async (conversationId, messageId, inferenceId, jobId) => {
    try {
      const { job: updatedJob, conversation } = await inferencesApi.pollJob(inferenceId, jobId);

      if (['completed', 'failed', 'cancelled'].includes(updatedJob?.status)) {
        // Async settlement happens after the original chat response. Refresh
        // the balance even when the poll includes a full conversation.
        try {
          await getStores().useAuthStore.getState().fetchUserCredits({ force: true });
        } catch (error) {
          console.warn('[pollInferenceJob] Failed to refresh credits:', error);
        }
      }

      // If server returned the full conversation, prefer store's normalized addChat path
      if (conversation) {
        get().addChat(conversation);
        return updatedJob;
      }

      // Otherwise, update the specific job inside the message's inferences locally
      set(state => ({
        chats: state.chats.map(c => {
          // If server returned a fresh conversation, prefer that for this chat
          const isTargetChat = c.id === conversationId;
          if (!isTargetChat) return c;

          return {
            ...c,
            messages: c.messages.map(m => {
              if (m.id !== messageId) return m;
              const nextInferences = (m.inferences || []).map(inf => {
                if (inf.id !== inferenceId) return inf;
                const nextJobs = (inf.output?.jobs || []).map(job =>
                  (job.id === jobId ? { ...job, ...updatedJob } : job)
                );
                return {
                  ...inf,
                  output: {
                    ...(inf.output || {}),
                    jobs: nextJobs,
                  }
                };
              });
              return { ...m, inferences: nextInferences };
            })
          };
        })
      }));

      // If the job just completed, fetch the full conversation to get the generated files
      // This is necessary because the poll endpoint may not return the conversation with files
      // when the job completes (e.g., video generation), causing the UI to show completion
      // but without displaying the generated media until a page refresh
      const jobStatus = (updatedJob?.status || '').toLowerCase();
      if (jobStatus === 'completed' && conversationId) {
        try {
          const freshConversation = await chatsApi.getConversationById(conversationId);
          if (freshConversation) {
            get().addChat(freshConversation);
          }
        } catch (fetchError) {
          // Silently fail - the job status is already updated, files will appear on next page load
          console.warn('[pollInferenceJob] Failed to fetch conversation after job completion:', fetchError);
        }
      }

      return updatedJob;
    } catch (error) {
      console.error('Error polling inference job:', error);
      // A transport/status-check error is not a failed generation. Preserve
      // the last server status so polling continues to an authoritative result.
      throw error;
    }
  },

  // Remove a specific message from a chat
  removeMessage: (conversationId, messageId) => {
    return set(state => ({
      chats: state.chats.map(c => 
        c.id === conversationId 
          ? {
              ...c,
              messages: c.messages.filter(msg => msg.id !== messageId)
            }
          : c
      )
    }));
  },

  hideMessage: (conversationId, messageId) => set(state => ({
    chats: state.chats.map(c => 
      c.id === conversationId 
        ? {
            ...c,
            messages: c.messages.map(msg => 
              msg.id === messageId
                ? { ...msg, isHidden: true }
                : msg
            )
          }
        : c
    )
  })),

  saveMessage: async (conversationId, messageId) => {
    try {
      await chatsApi.saveMessage(conversationId, messageId);
      
      // Update local state
      set(state => ({
        chats: state.chats.map(c => 
          c.id === conversationId 
            ? {
                ...c,
                messages: c.messages.map(msg => 
                  msg.id === messageId
                    ? { 
                        ...msg, 
                        flags: { 
                          ...msg.flags,
                          isSaved: true 
                        }
                      }
                    : msg
                )
              }
            : c
        )
      }));
    } catch (error) {
      console.error('Error saving message:', error);
      throw error;
    }
  },

  unsaveMessage: async (conversationId, messageId) => {
    try {
      await chatsApi.unsaveMessage(conversationId, messageId);
      
      // Update local state
      set(state => ({
        chats: state.chats.map(c => 
          c.id === conversationId 
            ? {
                ...c,
                messages: c.messages.map(msg => 
                  msg.id === messageId
                    ? { 
                        ...msg, 
                        flags: { 
                          ...msg.flags,
                          isSaved: false 
                        }
                      }
                    : msg
                )
              }
            : c
        )
      }));
    } catch (error) {
      console.error('Error unsaving message:', error);
      throw error;
    }
  },

  setMessagePreference: async (conversationId, messageId, preferred) => {
    try {
      // Get current message's parentMessageId for optimistic update
      const currentChat = get().chats.find(c => c.id === conversationId);
      const targetMessage = currentChat?.messages.find(m => m.id === messageId);
      const parentMessageId = targetMessage?.parentMessageId;

      // Optimistic update
      set(state => ({
        chats: state.chats.map(c => 
          c.id === conversationId 
            ? {
                ...c,
                messages: c.messages.map(msg => {
                  if (msg.id === messageId) {
                    // Target message: set to 1 (preferred) or 0 (rejected)
                    return { 
                      ...msg, 
                      flags: { 
                        ...msg.flags,
                        isGroupedResponsePreferred: preferred ? 1 : 0,
                        hasSelectedPreferenceLocally: true, // Local flag to track selection
                      }
                    };
                  } else if (parentMessageId && msg.parentMessageId === parentMessageId) {
                    // Set all other messages with same parent:
                    // - isGroupedResponsePreferred to 0 if preferring, otherwise leave unchanged
                    // - hasSelectedPreferenceLocally to true for all in the group
                    return { 
                      ...msg, 
                      flags: { 
                        ...msg.flags, 
                        isGroupedResponsePreferred: preferred ? 0 : msg.flags?.isGroupedResponsePreferred,
                        hasSelectedPreferenceLocally: true,
                      } 
                    };
                  }
                  return msg;
                })
              }
            : c
        )
      }));

      const data = await chatsApi.setMessagePreference(conversationId, messageId, preferred);
      
      // Update local state with the changed messages from API response
      if (data.messages && Array.isArray(data.messages)) {
        set(state => ({
          chats: state.chats.map(c => 
            c.id === conversationId 
              ? {
                  ...c,
                  messages: c.messages.map(existingMsg => {
                    const updatedMsg = data.messages.find(m => m.id === existingMsg.id);
                    if (updatedMsg) {
                      return {
                        ...existingMsg,
                        flags: {
                          ...existingMsg.flags,
                          ...updatedMsg.flags,
                          // Preserve the local selection flag
                          hasSelectedPreferenceLocally: existingMsg.flags?.hasSelectedPreferenceLocally,
                        }
                      };
                    }
                    return existingMsg;
                  })
                }
              : c
          )
        }));
      }

      return data;
    } catch (error) {
      console.error('Error setting message preference:', error);
      // On error, we could revert the optimistic update, but for now just throw
      throw error;
    }
  },

  editMessage: async (conversationId, messageId, content) => {
    try {
      if (typeof messageId === 'string' && messageId.startsWith('msg_')) {
        throw new Error('Cannot edit message that is still being processed. Please wait a moment and try again.');
      }

      const updatedMessage = await chatsApi.editMessage(conversationId, messageId, content);

      set(state => ({
        chats: state.chats.map(c =>
          c.id === conversationId
            ? {
                ...c,
                messages: c.messages.map(msg =>
                  msg.id === messageId
                    ? {
                        ...msg,
                        content: updatedMessage?.content ?? content,
                        isEdited: true,
                        editHistory: updatedMessage?.editHistory || msg.editHistory || [],
                      }
                    : msg
                )
              }
            : c
        )
      }));

      return updatedMessage;
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  },

  deleteMessage: async (conversationId, messageId) => {
    try {
      // Check if this is a temporary message ID (not yet synced with server)
      if (typeof messageId === 'string' && messageId.startsWith('msg_')) {
        throw new Error('Cannot delete message that is still being processed. Please wait a moment and try again.');
      }

      await chatsApi.deleteMessage(conversationId, messageId);
      
      // Update local state to mark message as deleted
      set(state => ({
        chats: state.chats.map(c => 
          c.id === conversationId 
            ? {
                ...c,
                messages: c.messages.map(msg => 
                  msg.id === messageId
                    ? { 
                        ...msg, 
                        content: 'This message has been deleted',
                        flags: { 
                          ...msg.flags,
                          isDeleted: true 
                        }
                      }
                    : msg
                )
              }
            : c
        )
      }));
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  },

  addChats: (conversations) => set(state => {
    // Drop hidden tool-run backing conversations at the gate so they can't
    // leak in from any source (bootstrap, pagination, search, socket push,
    // cross-tab rehydrate, etc.). Belt-and-braces with the backend filter.
    // Also drop Plus-locked placeholder rows — they're opaque shells (no
    // title, no messages, no `flags`) returned by churned-Plus search and
    // must never enter the canonical chats array or they'd render with
    // empty titles and crash code paths that assume full chat shape.
    // Also drop temp chats we just cleaned up on navigate-away so a late
    // conversation_created socket event cannot resurrect them into history.
    const recentlyDeletedTempIds = state._recentlyDeletedTemporaryChatIds || {};
    const now = Date.now();
    conversations = (conversations || []).filter(c => {
      if (!c || c.isPlusLocked || isHiddenToolRunChat(c)) return false;
      const deletedAt = recentlyDeletedTempIds[c.id];
      if (deletedAt && now - deletedAt <= 60_000) return false;
      return true;
    });

    // Continuously scrub the existing chats array too. Without this, stale
    // tool-run chats persisted in localStorage from BEFORE the filter
    // existed would survive forever — the rehydrate-time purge only runs
    // once per app boot, and a buggy code path could `setState({chats:…})`
    // around our filter. This is the cheapest, most reliable safety net.
    let scrubbedChats = state.chats;
    if (Array.isArray(state.chats) && state.chats.length > 0) {
      const cleaned = state.chats.filter(c => !isHiddenToolRunChat(c));
      if (cleaned.length !== state.chats.length) {
        scrubbedChats = cleaned;
      }
    }

    if (conversations.length === 0) {
      // Nothing new to add, but we may still have removed stale entries.
      return scrubbedChats === state.chats ? state : { ...state, chats: scrubbedChats };
    }
    state = scrubbedChats === state.chats ? state : { ...state, chats: scrubbedChats };

    const currentUser = getStores().useAuthStore.getState().user;
    const pinnedIds = [...new Set((currentUser?.pinnedConversations || []).map(asEntityId).filter(Boolean))];
    const pinnedSet = pinnedIdSetFrom(pinnedIds);

    // Defer cross-store updates (agents, models, files) so the chat list renders first.
    // These are not needed for rendering the chat sidebar.
    setTimeout(() => {
      const agents = conversations.map(conv => conv.agent).filter(Boolean);
      if (agents.length > 0) {
        getStores().useAgentStore.getState().addAgents(agents);
      }

      const modelMap = new Map();
      const agentMap = new Map();
      const fileMap = new Map();

      conversations.forEach(conv => {
        (conv.messages || []).forEach(msg => {
          if (msg.models) {
            msg.models.forEach(model => {
              if (model && model.id) modelMap.set(model.id, model);
            });
          }
          if (msg.agents) {
            msg.agents.forEach(agent => {
              if (agent && agent.id) agentMap.set(agent.id, agent);
            });
          }
          if (msg.files) {
            msg.files.forEach(file => {
              if (file && file.id) fileMap.set(file.id, file);
            });
          }
        });
      });

      if (modelMap.size > 0) getStores().useModelStore.getState().addModels(Array.from(modelMap.values()));
      if (agentMap.size > 0) getStores().useAgentStore.getState().addAgents(Array.from(agentMap.values()));
      if (fileMap.size > 0) getStores().useFileStore.getState().actions.addFiles(Array.from(fileMap.values()));

      // Restore composer model selection after models are in the store.
      // ChatView may have tried earlier and no-op'd while models were still
      // missing (common for image models that aren't in favorites/featured).
      const inputStore = getStores().useChatInputStore.getState();
      conversations.forEach(conv => {
        if (conv?.id && (conv.messages || []).length > 0) {
          inputStore.seedModelsFromConversationHistory(conv.id);
        }
      });
    }, 0);

    const chats = conversations.map(conv => {
      // Messages may be absent for summary-format conversations (e.g., initial load)
      const messages = conv.messages || [];
      const activeMessageCount = conv.contextWindow?.activeMessageCount ?? messages.filter(msg => msg.role !== 'file').length;
      const archivedMessageCount = conv.contextWindow?.archivedMessageCount || conv.stats?.archivedMessages || 0;
      // Get the last message timestamp
      const lastMessage = messages[messages.length - 1];
      const lastMessageTime = lastMessage
        ? getRelativeTime(lastMessage.date)
        : (conv.updatedAt ? getRelativeTime(conv.updatedAt) : '');

      return {
        id: conv.id,
        ...(Object.prototype.hasOwnProperty.call(conv, 'agent') ? { agent: conv.agent } : {}),
        ...(Object.prototype.hasOwnProperty.call(conv, 'agentId') ? { agentId: conv.agentId } : {}),
        ...(Object.prototype.hasOwnProperty.call(conv, 'lastMessage') ? { lastMessage: conv.lastMessage } : {}),
        ...(Object.prototype.hasOwnProperty.call(conv, 'deletedAt') ? { deletedAt: conv.deletedAt } : {}),
        title: sanitizeChatTitle(conv.title),
        href: `/c/${conv.id}`,
        forkedFrom: conv.forkedFrom || null,
        inCollectionId: conv.inCollectionId,
        // Pending "Move this chat to <collection>?" suggestion. Serialized
        // payloads always carry the field (null when nothing is pending);
        // locally-built payloads may omit it entirely — keep that distinction
        // (undefined = non-authoritative) for the merge step below.
        collectionSuggestion: Object.prototype.hasOwnProperty.call(conv, 'collectionSuggestion')
          ? (conv.collectionSuggestion || null)
          : undefined,
        isArchived: conv?.flags?.isArchived || false,
        isInferencing: false,
        isInferencingWaiting: false,
        visibility: conv.visibility,
        coworkerSession: conv.coworkerSession || null,
        workSync: conv.workSync || null,
        isPinned: pinnedSet.has(asEntityId(conv.id)),
        lastMessageTime,
        unreadCount: conv.unreadCount || 0,
        user: conv.user,
        isOwner: (() => {
          const incomingUserId = asEntityId(conv.user);
          if (!incomingUserId || !currentUser?.id) return undefined;
          return sameEntityId(incomingUserId, currentUser.id);
        })(),
        createdAt: conv.createdAt,
        updatedAt: conv.updatedAt,
        flags: {
          // isArchived: conv.flags?.isArchived || false,
          // isInferencing: conv.flags?.isInferencing || false,
          isFlagged: conv.flags?.isFlagged || false,
          // createdByApi: conv.flags?.createdByApi || false,
          // Only set isTemporary when the payload explicitly carries it.
          // Forcing `|| false` here demotes real temp chats during merges
          // when a partial update omits the flag, which makes them show up
          // in native chat history and skips navigate-away cleanup.
          ...(conv.flags && Object.prototype.hasOwnProperty.call(conv.flags, 'isTemporary')
            ? { isTemporary: !!conv.flags.isTemporary }
            : {}),
          ...(conv.flags && Object.prototype.hasOwnProperty.call(conv.flags, 'usePersonalization')
            ? { usePersonalization: !!conv.flags.usePersonalization }
            : {}),
          fastMode: conv.flags?.fastMode || false,
          isCoworkerSession: conv.flags?.isCoworkerSession || false,
        },
        // Stash whether incoming flags included isTemporary so the merge
        // step can preserve an existing true when the field was absent.
        _incomingHasIsTemporary: !!(conv.flags && Object.prototype.hasOwnProperty.call(conv.flags, 'isTemporary')),
        _incomingHasUsePersonalization: !!(conv.flags && Object.prototype.hasOwnProperty.call(conv.flags, 'usePersonalization')),
        stats: {
          totalTokens: conv.stats?.totalTokens || 0,
          activeContextTokens: conv.stats?.activeContextTokens || 0,
          archivedMessages: archivedMessageCount,
        },
        contextWindow: {
          ...(conv.contextWindow || {}),
          activeMessageCount,
          archivedMessageCount,
          messageCount: conv.contextWindow?.messageCount ?? (activeMessageCount + archivedMessageCount),
        },
        archivePagination: {
          isLoading: false,
          hasMore: archivedMessageCount > 0,
          nextBeforeIndex: null,
          loadedCount: 0,
          error: null,
        },
        // PLUS_WINBACK_PRESSURE §2: when the conversation detail
        // endpoint truncates messages for a churned-Plus user, it
        // attaches a `plusMessageLock` payload describing how many
        // messages were hidden. ChatView reads this to render the
        // gradient "N earlier messages locked" banner above the
        // visible tail. The detail-fetch path tags the payload with
        // `_plusMessageLockAuthoritative` so the merge step below
        // can distinguish "we just got a definitive lock state"
        // (which can also clear a stale lock via `null`) from a
        // list-endpoint payload that carries no per-conversation
        // lock info — list refreshes must NOT clobber a previously
        // captured truncation banner.
        ...(conv._plusMessageLockAuthoritative
            ? { plusMessageLock: conv.plusMessageLock || null }
            : {}),
        // Summary payloads commonly use messages: []; never let a draft turn
        // those into detail payloads and replace an open chat's full history.
        messages: (messages || []).length > 0
          ? mergeStreamingDraftMessages(
            conv.id,
            messages.map(msg => mapSerializedMessageToMessage(msg)),
          )
          : messages.map(msg => mapSerializedMessageToMessage(msg)),
      };
    });

    // Upsert chats: merge new data into existing where IDs match
    const existingChats = new Map(state.chats.map(c => [asEntityId(c.id), c]));
    const updatedOrInserted = chats.map(newChat => {
      const existing = existingChats.get(asEntityId(newChat.id));
      if (!existing) {
        const { _incomingHasIsTemporary, _incomingHasUsePersonalization, ...cleanNewChat } = newChat;
        return {
          ...cleanNewChat,
          isOwner: cleanNewChat.isOwner ?? false,
          flags: {
            ...(cleanNewChat.flags || {}),
            // New inserts default missing isTemporary to false.
            isTemporary: !!cleanNewChat.flags?.isTemporary,
            usePersonalization: !!cleanNewChat.flags?.usePersonalization,
          },
        };
      }

      // Create maps for existing messages - by ID and by content (for user messages)
      const existingMessagesById = new Map(
        (existing.messages || []).filter(m => m.id).map(m => [m.id, m])
      );
      
      // Also create a map of user messages by content for fallback matching
      // This handles the case where the local message has id: null but the backend
      // sends the same message with a server ID
      const existingUserMessagesByContent = new Map(
        (existing.messages || [])
          .filter(m => m.role === 'user' && m.content)
          .map(m => [m.content, m])
      );

      // Merge messages: preserve user message modelIds from existing messages
      // This is important because the backend may return different modelIds due to
      // auto-routing (e.g., detecting "draw me a cat" and using image-generator),
      // but we want to keep the user's original model selection in the UI
      const newMessageIds = new Set((newChat.messages || []).map(m => m.id).filter(Boolean));
      const isWorkChat = !!(
        existing.flags?.isCoworkerSession
        || newChat.flags?.isCoworkerSession
        || existing.coworkerSession
        || newChat.coworkerSession
      );
      
      const mergedMessages = (newChat.messages || []).map(newMsg => {
        if (isWorkChat) {
          if (newMsg.role !== 'user') return newMsg;
          const stripped = stripWorkInjectedPrompt(newMsg.content);
          return (stripped !== newMsg.content) ? { ...newMsg, content: stripped } : newMsg;
        }
        // Try to find existing message by ID first
        let existingMsg = existingMessagesById.get(newMsg.id);
        
        // For user messages, also try matching by content if ID match failed
        // This handles timing issues where the local message has id: null
        if (!existingMsg && newMsg.role === 'user' && newMsg.content) {
          existingMsg = existingUserMessagesByContent.get(newMsg.content);
        }
        
        if (existingMsg) {
          // Preserve _stableKey so FlatList doesn't treat the message as
          // removed-then-added (which causes flashing on native).
          const preserved = {};
          if (existingMsg._stableKey) preserved._stableKey = existingMsg._stableKey;

          // For user messages, preserve the original modelIds if they exist
          if (newMsg.role === 'user' && Array.isArray(existingMsg.modelIds) && existingMsg.modelIds.length > 0) {
            preserved.modelIds = existingMsg.modelIds;
          }

          // Intent routing may persist a different inference model; keep the
          // user's display attribution if the incoming payload omitted it.
          const existingDisplayIds = existingMsg.parameters?.___displayModelIds;
          const incomingDisplayIds = newMsg.parameters?.___displayModelIds;
          if (
            Array.isArray(existingDisplayIds) && existingDisplayIds.length > 0 &&
            !(Array.isArray(incomingDisplayIds) && incomingDisplayIds.length > 0)
          ) {
            preserved.parameters = {
              ...(newMsg.parameters || {}),
              ___displayModelIds: existingDisplayIds,
            };
          }

          // Don't let an empty/missing inferences payload from the backend wipe
          // out a locally-tracked pending inference job. Without this, polling
          // /pollJob (which calls addChat→addChats) can momentarily flip the
          // message into "no jobs" → in-progress card disappears mid-generation.
          const authoritativeInferences = pickAuthoritativeInferences(existingMsg.inferences, newMsg.inferences);
          if (authoritativeInferences !== newMsg.inferences) {
            preserved.inferences = authoritativeInferences;
          }

          // A stale detail/list fetch can land after stream_end with empty
          // (or mid-persist truncated) text. Streaming flags are already
          // cleared by then, so without this the bubble fails
          // shouldDisplayMessage and the whole reply vanishes.
          const authoritativeContent = pickAuthoritativeMessageText(existingMsg.content, newMsg.content);
          if (authoritativeContent !== newMsg.content) {
            preserved.content = authoritativeContent;
          }
          const authoritativeThinking = pickAuthoritativeMessageText(
            existingMsg.thinkingContent,
            newMsg.thinkingContent
          );
          if (authoritativeThinking !== newMsg.thinkingContent) {
            preserved.thinkingContent = authoritativeThinking;
          }

          // For assistant messages still generating media (images, videos,
          // songs, 3D models — gpt-image-2 high quality can take 2-5 min,
          // Fal video jobs can take a few minutes), preserve the in-flight
          // flags. The backend's persisted message doc doesn't carry
          // isRequestingMedia / isImage / streaming flags, so a plain
          // `newMsg` would wipe them and cause the loading placeholder to
          // vanish while the media artifact is still being generated
          // upstream — which is exactly the "blank chat for ~30s after
          // stream_end" regression.
          if (newMsg.role === 'assistant') {
            // What kind of media (if any) the existing message is waiting on
            // — null if it's not a media-generating message.
            const requestedKind = existingMsg.isRequestingMedia
              ? String(existingMsg.isRequestingMedia).toLowerCase()
              : null;
            // Did the new payload already deliver the artifact we asked for?
            // We check against the SPECIFIC kind (not just images) so a video
            // landing actually clears the video placeholder. Falls back to
            // legacy image check when no kind is set.
            const newMsgHasRequestedMedia = requestedKind
              ? messageHasMediaOutput(newMsg, requestedKind)
              : messageHasImageOutput(newMsg);
            const newMsgIsError = !!(newMsg.isError || newMsg.hasError);

            // Preserve isRequestingMedia if the backend dropped it and the
            // message hasn't yet resolved to real media of the requested kind
            // or an error. Without this guard, the chat goes blank between
            // stream_end and the async file actually being attached.
            if (
              existingMsg.isRequestingMedia
              && !newMsg.isRequestingMedia
              && !newMsgHasRequestedMedia
              && !newMsgIsError
            ) {
              preserved.isRequestingMedia = existingMsg.isRequestingMedia;
            }

            // Preserve isImage (set when the image stream event arrived) if the
            // backend response lost it.
            if (existingMsg.isImage && !newMsg.isImage) {
              preserved.isImage = existingMsg.isImage;
            }

            if (shouldPreserveExistingMessageFiles(existingMsg, newMsg, newChat.messages) && !newMsgIsError) {
              preserved.files = existingMsg.files;
            }

            // Preserve in-flight streaming flags when the local copy thinks
            // we're still generating AND the new copy has nothing resolved.
            const existingInFlight = !!(
              existingMsg.isStreaming
              || existingMsg.isTyping
              || existingMsg.isWaiting
              || existingMsg.isThinking
            );
            const newInFlight = !!(
              newMsg.isStreaming
              || newMsg.isTyping
              || newMsg.isWaiting
              || newMsg.isThinking
            );
            const newMsgHasResolvedContent = !!(
              (typeof newMsg.content === 'string' && newMsg.content.trim().length > 0)
              || (typeof newMsg.thinkingContent === 'string' && newMsg.thinkingContent.trim().length > 0)
              || (Array.isArray(newMsg.files) && newMsg.files.length > 0)
              || (Array.isArray(newMsg.inferences) && newMsg.inferences.length > 0)
            );
            if (existingInFlight && !newInFlight && !newMsgHasRequestedMedia && !newMsgIsError && !newMsgHasResolvedContent) {
              if (existingMsg.isStreaming) preserved.isStreaming = true;
              if (existingMsg.isTyping) preserved.isTyping = true;
              if (existingMsg.isWaiting) preserved.isWaiting = true;
              if (existingMsg.isThinking) preserved.isThinking = true;
            }
          }

          if (Object.keys(preserved).length > 0) {
            return { ...newMsg, ...preserved };
          }
        }
        
        return newMsg;
      });

      // IMPORTANT: Preserve local streaming messages that aren't in the backend response yet
      // This prevents losing messages during streaming when addChats is called with stale backend data
      const localStreamingMessages = isWorkChat ? [] : (existing.messages || []).filter(localMsg => {
        // Only preserve assistant messages that are actively streaming/processing
        if (localMsg.role !== 'assistant') return false;
        if (!localMsg.isStreaming && !localMsg.isTyping && !localMsg.isWaiting && !localMsg.isThinking) return false;
        // Don't include if the message is already in the new data
        if (newMessageIds.has(localMsg.id)) return false;
        return true;
      });
      
      // Build a set of content+role keys from merged (backend) messages for dedup
      const mergedContentKeys = new Set(
        mergedMessages
          .filter(m => m.content && m.content.trim())
          .map(m => `${m.role}::${m.content}`)
      );

      // Also preserve local messages with content that aren't in the backend yet
      // This handles the case where a message was just created locally but hasn't been saved to backend
      const localMessagesWithContent = isWorkChat ? [] : (existing.messages || []).filter(localMsg => {
        // Skip if already in new messages (by ID)
        if (newMessageIds.has(localMsg.id)) return false;
        // Skip if already included as streaming message
        if (localStreamingMessages.some(sm => sm.id === localMsg.id)) return false;
        // Only include if message has actual content that would be lost
        const hasContent = localMsg.content && localMsg.content.length > 0;
        const hasImages = Array.isArray(localMsg.images) && localMsg.images.length > 0;
        const hasFiles = Array.isArray(localMsg.files) && localMsg.files.length > 0;
        if (!(hasContent || hasImages || hasFiles)) return false;
        // Long-context turns can spend more than 30 seconds preprocessing before
        // the backend response includes the new user message. While this chat is
        // actively inferencing, keep the optimistic user bubble visible until a
        // backend twin arrives by id or content. This must run before the
        // content-duplicate guard so repeated prompts with the same text still
        // render as distinct pending turns.
        const isPendingUserMessage = localMsg.role === 'user' && (
          !localMsg.id ||
          (typeof localMsg.id === 'string' && localMsg.id.startsWith('msg_') && !localMsg._id)
        );
        // Queued offline sends are never on the backend and the chat carries
        // no inferencing flags for them — without the isQueued clause a
        // soft-sync would drop the bubble while its queue entry lived on.
        if (isPendingUserMessage && (existing.isInferencing || existing.isInferencingWaiting || localMsg.isQueued)) {
          return true;
        }
        // Skip if backend already has a message with the same role+content
        // This prevents voice mode temp messages (voice-user-*) from being preserved
        // alongside their backend counterparts which have real server IDs
        if (localMsg.content && localMsg.content.trim()) {
          const contentKey = `${localMsg.role}::${localMsg.content}`;
          if (mergedContentKeys.has(contentKey)) return false;
        }
        // Voice transcript messages are produced asynchronously and persisted
        // to the backend via a separate fire-and-forget POST. If a save lost
        // the race with end-of-session cleanup, the message will be missing
        // from the backend response on refetch. The 30-second freshness
        // window below would then drop them — which is exactly the "all
        // voice transcripts disappeared after I ended the call" bug. Voice
        // messages are explicitly tagged (`isVoiceMessage` or
        // `parameters.source === 'voice'`); always keep them when they're
        // not represented by a backend twin.
        const isVoiceMessage = !!(
          localMsg.isVoiceMessage ||
          localMsg?.parameters?.source === 'voice'
        );
        if (isVoiceMessage) return true;
        // Only preserve recent messages (within last 30 seconds) to avoid keeping stale data
        const isRecent = localMsg.timestamp && (Date.now() - new Date(localMsg.timestamp).getTime()) < 30000;
        return isRecent;
      });

      const existingArchivedMessages = (existing.messages || []).filter(localMsg => {
        if (!localMsg?.isArchivedMessage) return false;
        if (newMessageIds.has(localMsg.id)) return false;
        return true;
      });


      const finalMessagesById = new Set();
      const finalMessages = [
        ...existingArchivedMessages,
        ...mergedMessages,
        ...localStreamingMessages,
        ...localMessagesWithContent,
      ].filter(message => {
        const messageId = message?.id || message?._id;
        if (!messageId) return true;
        if (finalMessagesById.has(messageId)) return false;
        finalMessagesById.add(messageId);
        return true;
      });

      // Merge: prefer new data; preserve transient UI fields and pinned state
      // If incoming conversation has no messages (summary format from initial load),
      // preserve existing messages to avoid wiping loaded conversation data
      const shouldPreserveExistingMessages = newChat.messages.length === 0 && (existing.messages || []).length > 0;

      // Preserve the existing updatedAt to prevent chats from jumping to the top
      // of the sidebar when simply viewing them. The only time we should update
      // updatedAt is when a genuinely new message arrives (handled by addMessage
      // and stream_end, not by addChats merge). Loading a conversation's messages
      // for the first time (hydrating a summary chat) is NOT new content.
      const archivedMessageCount = newChat.contextWindow?.archivedMessageCount || 0;
      const previousArchivedMessageCount = existing.contextWindow?.archivedMessageCount
        || existing.stats?.archivedMessages
        || 0;
      const archivedMessageCountGrew = archivedMessageCount > previousArchivedMessageCount;
      const existingArchivePagination = existing.archivePagination || {};
      const existingLoadedArchivedCount = existingArchivePagination.loadedCount
        || existingArchivedMessages.length
        || 0;

      const incomingHasIsTemporary = newChat._incomingHasIsTemporary === true;
      const incomingHasUsePersonalization = newChat._incomingHasUsePersonalization === true;
      const {
        _incomingHasIsTemporary: _dropIncomingHasIsTemporary,
        _incomingHasUsePersonalization: _dropIncomingHasUsePersonalization,
        ...newChatWithoutMeta
      } = newChat;

      // Inference-state protection for the merge below. REST payloads
      // hard-code isInferencing/isInferencingWaiting to false (see the mapper
      // above), so any addChats landing mid-turn clobbers a locally-true flag
      // unless explicitly protected. Two windows need protection:
      //   1. Active stream — an assistant message is streaming/typing/waiting/
      //      thinking (the pre-existing guard).
      //   2. Preprocessing — the user just sent and no assistant message
      //      exists yet: either the optimistic pending bubble (msg_* id) is
      //      still unacknowledged, or the last message is an acked user
      //      message whose assistant reply hasn't arrived. Without window 2,
      //      any soft-sync / detail fetch / poll during preprocessing blanks
      //      the thinking UI — the "thinking state drops, looks stopped, then
      //      restarts when stream_start arrives" flicker.
      // Window 2 is time-bounded so a genuinely stuck flag can still be
      // healed by a later backend refresh once it expires.
      const existingMessages = existing.messages || [];
      const hasLocalActiveStream = existingMessages.some(m =>
        m.role === 'assistant' &&
        (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
      );
      const hasLocalInFlightTurn = (() => {
        if (hasLocalActiveStream) return false;
        if (!existing.isInferencing && !existing.isInferencingWaiting) return false;
        const hasPendingUserBubble = existingMessages.some(m =>
          m.role === 'user' &&
          typeof m.id === 'string' &&
          m.id.startsWith('msg_') &&
          !m._id
        );
        if (hasPendingUserBubble) return true;
        const lastMessage = existingMessages[existingMessages.length - 1];
        if (lastMessage?.role !== 'user') return false;
        const lastMessageTs = Date.parse(lastMessage.timestamp || '');
        if (Number.isNaN(lastMessageTs)) return false;
        return (Date.now() - lastMessageTs) < IN_FLIGHT_TURN_MERGE_GUARD_MS;
      })();
      const protectLocalInferenceState = hasLocalActiveStream || hasLocalInFlightTurn;

      return {
        ...existing,
        ...newChatWithoutMeta,
        // inCollectionId is the authoritative "this chat lives in a collection"
        // flag the sidebar uses to hide collected chats from "Your Chats".
        // Some incoming payloads (socket/stream pushes, summary refetches, or
        // fetches that race ahead of the collections load) arrive WITHOUT the
        // field at all. A bare {...newChat} spread lets that absent/undefined
        // value clobber a known-good membership, so the chat wrongly reappears
        // in "Your Chats" after a refresh.
        //
        // Distinguish "explicitly null" from "missing":
        //  - field present (incl. null)  -> authoritative; honor it. This keeps
        //    removal working: remove-from-collection persists null and the next
        //    fetch carries inCollectionId: null, which must un-hide the chat.
        //  - field absent (undefined)    -> non-authoritative; keep existing.
        inCollectionId: Object.prototype.hasOwnProperty.call(newChat, 'inCollectionId')
          && newChat.inCollectionId !== undefined
          ? newChat.inCollectionId
          : (existing.inCollectionId ?? null),
        // Same authoritative-vs-absent rule as inCollectionId: undefined means
        // the incoming payload didn't carry the field, so keep what we have.
        collectionSuggestion: newChat.collectionSuggestion !== undefined
          ? newChat.collectionSuggestion
          : (existing.collectionSuggestion ?? null),
        flags: {
          ...(existing.flags || {}),
          ...(newChatWithoutMeta.flags || {}),
          // Never demote a temp chat to permanent because a partial payload
          // omitted flags.isTemporary — that leaves zombies in native history.
          isTemporary: incomingHasIsTemporary
            ? !!newChatWithoutMeta.flags?.isTemporary
            : !!(existing.flags?.isTemporary || newChatWithoutMeta.flags?.isTemporary),
          usePersonalization: incomingHasUsePersonalization
            ? !!newChatWithoutMeta.flags?.usePersonalization
            : !!(existing.flags?.usePersonalization || newChatWithoutMeta.flags?.usePersonalization),
        },
        updatedAt: existing.updatedAt || newChat.updatedAt,
        contextWindow: {
          ...(existing.contextWindow || {}),
          ...(newChat.contextWindow || {}),
        },
        archivePagination: {
          ...(newChat.archivePagination || {}),
          ...existingArchivePagination,
          isLoading: false,
          loadedCount: existingLoadedArchivedCount,
          hasMore: archivedMessageCountGrew
            || (existingArchivePagination.hasMore !== false && archivedMessageCount > existingLoadedArchivedCount),
          error: existingArchivePagination.error || null,
        },
        messages: shouldPreserveExistingMessages ? existing.messages : finalMessages,
        // Auth pinnedConversations is the source of truth — never sticky-OR
        // an older local isPinned, or unpinned chats stay sorted with pinned.
        isPinned: pinnedSet.has(asEntityId(newChat.id)),
        user: newChat.user ?? existing.user ?? null,
        isOwner: newChat.isOwner !== undefined ? newChat.isOwner : (existing.isOwner ?? false),
        coworkerSession: newChat.coworkerSession || existing.coworkerSession || null,
        workSync: newChat.workSync || existing.workSync || null,
        // Backend is the source of truth for inferencing state — but only when
        // there's no locally in-flight turn to protect (see
        // protectLocalInferenceState above).
        //
        // A previous version used `existing ?? newChat`, which meant a
        // locally-stuck `isInferencing: true` (e.g. from a missed
        // `conversation_stream_end` during a socket blip, with no streaming
        // assistant message left) could never be cleared by a backend refresh
        // carrying `isInferencing: false`. That left the chat stuck loading
        // forever, which in turn made `emitSendUserMessage` reject every new
        // send via the "already processing" guard.
        //
        // We preserve the local `true` while a turn is in flight — the backend
        // can transiently report `isInferencing: false` mid-stream (e.g.
        // between media-generation extraction steps, see
        // `_recoverStalledConversation`'s media guard) and REST payloads
        // hard-code both flags to false during preprocessing. When no turn is
        // in flight locally, a stuck `true` is just stale state — let the
        // backend's explicit boolean clear it.
        isInferencing: existing.flags?.isCoworkerSession
          ? (existing.isInferencing ?? false)
          : protectLocalInferenceState
            ? (existing.isInferencing ?? newChat.isInferencing)
            : (newChat.isInferencing ?? existing.isInferencing),
        isInferencingWaiting: existing.flags?.isCoworkerSession
          ? (existing.isInferencingWaiting ?? false)
          : protectLocalInferenceState
            ? (existing.isInferencingWaiting ?? newChat.isInferencingWaiting)
            : (newChat.isInferencingWaiting ?? existing.isInferencingWaiting),
      };
    });

    // Include any existing chats not present in the incoming list
    const incomingIds = new Set(chats.map(c => asEntityId(c.id)));
    const untouchedExisting = state.chats.filter(c => !incomingIds.has(asEntityId(c.id)));

    // Combine and sort
    const allChats = sortChatsWithPinned([...untouchedExisting, ...updatedOrInserted], pinnedIds);

    return { chats: allChats };
  }),

  // Update createConversation to use the store function
  createConversation: async ({ userId, formattedMessage, collectionId, isTemporary = false, usePersonalization = false }) => {
    try {
      // Log the incoming fast mode status
      console.log('[createConversation] Starting:', {
        formattedMessageFastMode: formattedMessage?.fastMode,
        hasFormattedMessage: !!formattedMessage,
        isTemporary,
        usePersonalization,
      });

      const conversation = await chatsApi.createConversation(formattedMessage, collectionId, isTemporary, usePersonalization);

      // The other half of the send funnel: follow-ups go out over the socket,
      // but a brand new chat is created here. Both report the same milestone,
      // which is deduped per account.
      trackActivationOnce(userId, ACTIVATION_ACTION.FIRST_MESSAGE_SENT);

      // Keep the client flag authoritative for the create path. Native home
      // used to send a stale isTemporary=false; once fixed we still pin the
      // flag we requested so navigate-away cleanup and sidebar filters work
      // even if a partial payload omits it.
      if (isTemporary) {
        conversation.flags = {
          ...(conversation.flags || {}),
          isTemporary: true,
          usePersonalization: !!usePersonalization,
        };
      }

      // If fast mode was enabled for this new conversation, set it on the conversation
      if (formattedMessage.fastMode) {
        console.log('[createConversation] Setting fastMode flag on conversation:', conversation.id);
        conversation.flags = {
          ...(conversation.flags || {}),
          fastMode: true,
        };
        // Also emit socket event to persist fast mode on the backend (silent - no toast)
        getSocket().emitToggleFastMode(userId, conversation.id, true, { silent: true });
      }

      // Log the conversation being added to store
      console.log('[createConversation] Adding to store:', {
        conversationId: conversation.id,
        conversationFlags: conversation.flags,
        fastModeSet: conversation.flags?.fastMode,
      });

      // Add new chats using the helper (which now includes proper sorting)
      get().addChats([conversation]);

      // Carry the composer's model selection into the new conversation's
      // input context. Without this, setContext(conversationId) creates a
      // fresh input state seeded with the default text model — so picking
      // e.g. GPT Image 2 on the home screen silently reverts to Auto/default
      // for every follow-up message in the conversation.
      // Quick-create auto-sends (Image/Video/Music buttons) set
      // _skipModelTransfer because they're one-shot generations that should
      // reset to Auto — mark the new context explicit-Auto so neither this
      // seeding nor the later history seeding pins the generation model.
      try {
        const inputStore = getStores().useChatInputStore.getState();
        if (formattedMessage?._skipModelTransfer) {
          inputStore.markContextExplicitAuto(conversation.id);
        } else if (Array.isArray(formattedMessage?.modelIds) && formattedMessage.modelIds.length > 0) {
          inputStore.seedConversationModels(conversation.id, formattedMessage.modelIds);
        } else {
          // Home/new-chat Auto (empty modelIds) must carry into the thread.
          // Otherwise setContext seeds the default text model and follow-ups
          // leave Auto — which is what makes the model card "randomly" appear.
          inputStore.markContextExplicitAuto(conversation.id);
        }
      } catch (seedError) {
        console.warn('[createConversation] Could not seed model selection:', seedError);
      }

      if (collectionId) {
        getStores().useCollectionStore.getState().addConversationToCollectionLocally(collectionId, conversation.id);
      }

      // If title is empty or "untitled chat", start title retrieval process
      if (!conversation.title || isPlaceholderChatTitle(conversation.title)) {
        get().retryTitleRetrieval(userId, conversation.id);
      }

      return conversation;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  },

  saveTemporaryChat: async (conversationId) => {
    const conversation = await chatsApi.saveTemporaryChat(conversationId);
    if (conversation) {
      conversation.flags = {
        ...(conversation.flags || {}),
        isTemporary: false,
      };
      get().addChats([conversation]);
    }
    return conversation;
  },

  setTemporaryChatPersonalization: async (conversationId, usePersonalization) => {
    const conversation = await chatsApi.setTemporaryChatPersonalization(conversationId, usePersonalization);
    if (conversation) {
      const existing = get().chats.find(c => c.id === conversationId);
      get().updateLocalChatDetails(conversationId, {
        flags: {
          ...(existing?.flags || {}),
          ...(conversation.flags || {}),
          isTemporary: true,
          usePersonalization: !!usePersonalization,
        },
      });
    }
    return conversation;
  },

  createFirstConversation: async (formattedMessage) => {
    try {
      const { conversation, user, token } = await chatsApi.createFirstConversation(formattedMessage);

      // Note: We don't need to initialize socket here since setToken already does it
      // getSocket().connect(token);

      // If fast mode was enabled for this new conversation, set it on the conversation
      if (formattedMessage.fastMode) {
        conversation.flags = {
          ...(conversation.flags || {}),
          fastMode: true,
        };
        // Also emit socket event to persist fast mode on the backend (after a short delay for socket to connect, silent - no toast)
        setTimeout(() => {
          getSocket().emitToggleFastMode(user.id, conversation.id, true, { silent: true });
        }, 600);
      }

      // Add new chats using the helper (which now includes proper sorting)
      get().addChats([conversation]);

      // Wait a bit for socket to connect before attempting title retrieval
      setTimeout(() => {
        // If title is empty or "untitled chat", start title retrieval process
        if (!conversation.title || isPlaceholderChatTitle(conversation.title)) {
          get().retryTitleRetrieval(user.id, conversation.id);
        }
      }, 500);

      return { conversation, user, token };
    } catch (error) {
      console.error('Error creating first conversation:', error);
      throw error;
    }
  },

  fetchChats: async (page = 1) => {
    if (page === 1 && get()._fetchChatsPromise) return get()._fetchChatsPromise;

    const promise = (async () => {
    try {
      const authState = getStores().useAuthStore.getState();
      if (!authState.token) return [];

      const { filters } = get();
      const options = {
        filter: filters.type,
        sort: filters.sort,
        inCollectionId: filters.inCollectionId,
        withoutCollections: filters.withoutCollections
      };

      const { chats, pagination, plusLock } = await chatsApi.fetchChats(page, options);

      if (page === 1) {
        const preservedPinned = pinnedIdSetFrom(getStores().useAuthStore.getState().user?.pinnedConversations);
        const incomingIds = new Set(chats.map(chat => asEntityId(chat.id)));
        const filterType = filters.type;
        set((state) => ({
          chats: state.chats.filter((c) => {
            // Keep matching rows until addChats merges the metadata-only refresh;
            // otherwise it discards cached messages and explicit bot associations.
            if (incomingIds.has(asEntityId(c.id))) return true;
            if (!preservedPinned.has(asEntityId(c.id))) return false;
            if (filterType === 'work') return c.flags?.isCoworkerSession === true;
            if (filterType === 'non-work') return !c.isArchived && c.flags?.isCoworkerSession !== true;
            if (filterType === 'archived') return c.isArchived === true;
            if (filterType === 'shared' || filterType === 'public') return c.visibility === 'public';
            return !c.isArchived;
          }),
        }));
      }
      
      // Add new chats using the helper (which now includes proper sorting)
      get().addChats(chats);
      
      // Update pagination and preserve filter settings
      set(state => ({
        pagination: {
          ...pagination,
          filter: state.filters.type,
          sort: state.filters.sort,
          inCollectionId: state.filters.inCollectionId,
          withoutCollections: state.filters.withoutCollections
        },
        // Persist plusLock only on page 1 (subsequent pages return no
        // plusLock; we shouldn't accidentally clear it then). For
        // unaffected users the backend never sends the payload so this
        // stays null and the sidebar UI is byte-identical to today.
        ...(page === 1 ? { plusLock: plusLock || null } : {}),
      }));

      return chats;
    } catch (error) {
      console.error('Error fetching chats:', error);
      throw error;
    }
    })();

    if (page === 1) {
      set({ _fetchChatsPromise: promise });
      promise.finally(() => {
        if (get()._fetchChatsPromise === promise) set({ _fetchChatsPromise: null });
      });
    }
    return promise;
  },

  fetchArchivedMessages: async (conversationId, options = {}) => {
    const currentChat = get().chats.find(c => c.id === conversationId);
    const currentPagination = currentChat?.archivePagination || {};
    if (!currentChat || currentPagination.isLoading) return [];

    const beforeIndex = options.beforeIndex !== undefined
      ? options.beforeIndex
      : currentPagination.nextBeforeIndex;
    const limit = options.limit || 50;

    set(state => ({
      chats: state.chats.map(chat => chat.id === conversationId
        ? {
            ...chat,
            archivePagination: {
              ...(chat.archivePagination || {}),
              isLoading: true,
              error: null,
            },
          }
        : chat
      )
    }));

    try {
      const response = await chatsApi.getArchivedConversationMessages(conversationId, {
        beforeIndex,
        limit,
      });
      const rawMessages = response.messages || [];

      setTimeout(() => {
        const modelMap = new Map();
        const agentMap = new Map();
        const fileMap = new Map();

        rawMessages.forEach(msg => {
          (msg.models || []).forEach(model => {
            if (model && model.id) modelMap.set(model.id, model);
          });
          (msg.agents || []).forEach(agent => {
            if (agent && agent.id) agentMap.set(agent.id, agent);
          });
          (msg.files || []).forEach(file => {
            if (file && file.id) fileMap.set(file.id, file);
          });
        });

        if (modelMap.size > 0) getStores().useModelStore.getState().addModels(Array.from(modelMap.values()));
        if (agentMap.size > 0) getStores().useAgentStore.getState().addAgents(Array.from(agentMap.values()));
        if (fileMap.size > 0) getStores().useFileStore.getState().actions.addFiles(Array.from(fileMap.values()));
      }, 0);

      const mappedMessages = rawMessages.map(msg => ({
        ...mapSerializedMessageToMessage(msg),
        isArchivedMessage: true,
        archiveOriginalIndex: msg.archive?.originalIndex ?? null,
        archiveCompactedAt: msg.archive?.compactedAt ?? null,
      }));

      set(state => ({
        chats: state.chats.map(chat => {
          if (chat.id !== conversationId) return chat;

          const existingMessages = chat.messages || [];
          const existingIds = new Set(existingMessages.map(message => message.id || message._id).filter(Boolean));
          const newMessages = mappedMessages.filter(message => {
            const messageId = message.id || message._id;
            return !messageId || !existingIds.has(messageId);
          });
          const loadedCount = (chat.archivePagination?.loadedCount || 0) + newMessages.length;
          const archivedMessageCount = chat.contextWindow?.archivedMessageCount
            || chat.stats?.archivedMessages
            || response.pagination?.archivedMessageCount
            || 0;

          return {
            ...chat,
            messages: [...newMessages, ...existingMessages],
            contextWindow: {
              ...(chat.contextWindow || {}),
              archivedMessageCount,
            },
            archivePagination: {
              ...(chat.archivePagination || {}),
              isLoading: false,
              error: null,
              loadedCount,
              nextBeforeIndex: options.preserveCursor === true
                ? (chat.archivePagination?.nextBeforeIndex ?? null)
                : (response.pagination?.nextBeforeIndex ?? null),
              hasMore: options.preserveCursor === true
                ? (chat.archivePagination?.hasMore !== false && loadedCount < archivedMessageCount)
                : (response.pagination?.hasMore === true && loadedCount < archivedMessageCount),
            },
          };
        })
      }));

      return mappedMessages;
    } catch (error) {
      set(state => ({
        chats: state.chats.map(chat => chat.id === conversationId
          ? {
              ...chat,
              archivePagination: {
                ...(chat.archivePagination || {}),
                isLoading: false,
                error: error.message || 'Failed to load earlier messages',
              },
            }
          : chat
        )
      }));
      console.error('Error fetching archived messages:', error);
      throw error;
    }
  },

  // Drop locally cached chats the server has already soft-deleted. Native
  // persists the sidebar, so a phone that missed `conversation_deleted`
  // while backgrounded would otherwise keep showing those rows forever.
  reconcileDeletedChats: async ({ force = false } = {}) => {
    const existing = get()._reconcileDeletedChatsPromise;
    if (existing) return existing;

    const operation = (async () => {
      try {
        const authState = getStores().useAuthStore.getState();
        if (!authState.token) return false;

        const now = Date.now();
        if (!force && get()._lastDeletedChatPruneAt && now - get()._lastDeletedChatPruneAt < 15_000) {
          return true;
        }

        const ids = collectOwnedChatIds(get().chats);
        // Don't throttle an empty pass — persist hydration may still be
        // about to restore the cached sidebar.
        if (ids.length === 0) return true;

        const batches = [];
        for (let i = 0; i < ids.length; i += EXISTING_CHAT_IDS_BATCH) {
          batches.push(ids.slice(i, i + EXISTING_CHAT_IDS_BATCH));
        }
        const results = await Promise.all(
          batches.map((batch) => chatsApi.fetchExistingChatIds(batch)),
        );

        const alive = new Set();
        const checked = new Set();
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          for (let j = 0; j < batch.length; j++) checked.add(batch[j]);
          const existingIds = results[i].existingIds || [];
          for (let k = 0; k < existingIds.length; k++) alive.add(String(existingIds[k]));
        }

        const current = get().chats;
        const keepIds = new Set(
          filterChatsAgainstExistingIds(current, alive, checked).map(c => c.id),
        );
        for (let i = 0; i < current.length; i++) {
          if (!keepIds.has(current[i].id)) get().deleteChat(current[i].id);
        }
        set({ _lastDeletedChatPruneAt: Date.now() });
        return true;
      } catch (_) {
        return false;
      }
    })();

    // Attach cleanup after `operation` exists. Putting this in the async
    // function's `finally` block references `promise` before initialization
    // when the function returns before its first await (no token, throttled,
    // or an empty cache), leaving reconciliation stuck on a rejected promise.
    const promise = operation.finally(() => {
      if (get()._reconcileDeletedChatsPromise === promise) {
        set({ _reconcileDeletedChatsPromise: null });
      }
    });

    set({ _reconcileDeletedChatsPromise: promise });
    return promise;
  },

  softSyncChats: async ({ force = false } = {}) => {
    try {
      const authState = getStores().useAuthStore.getState();
      if (!authState.token) return;

      const { chats, pagination, plusLock } = await chatsApi.fetchChats(1);
      if (chats?.length > 0) {
        get().addChats(chats);
      }

      // Prefer an explicit existence check (covers pinned, in-collection,
      // archived, and chats past page 1). Fall back to the page-1 listing
      // window if that endpoint is not deployed yet.
      const reconciled = await get().reconcileDeletedChats({ force });
      if (reconciled) return;

      const pruned = filterChatsAgainstListingWindow({
        localChats: get().chats,
        serverChats: chats,
        pagination,
        plusLock,
      });
      if (pruned.length === get().chats.length) return;
      const keepIds = new Set(pruned.map(c => c.id));
      const current = get().chats;
      for (let i = 0; i < current.length; i++) {
        if (!keepIds.has(current[i].id)) get().deleteChat(current[i].id);
      }
    } catch (error) {
      // Soft sync is best-effort; don't let it break the app
    }
  },

  setSearchQuery: (query) => set({ searchQuery: query }),

  searchChats: async (query, page = 1) => {
    if (!query.trim()) {
      set({
        searchResults: [],
        searchLockedResults: [],
        searchPlusLock: null,
        searchPagination: {
          current: 1,
          pages: 1,
          total: 0,
          perPage: 20,
          hasMore: false
        },
        searchError: null
      });
      return;
    }

    try {
      set({ isSearching: true, searchError: null });
      const { chats, pagination, plusLock } = await chatsApi.searchChats(query, page);

      // Partition server response into normal matches (full chat docs)
      // and Plus-locked placeholders (`{ id, isPlusLocked: true, ... }`).
      // The latter are returned for churned-Plus users and must stay out
      // of the canonical chats array — see addChats guard.
      const normalChats = (chats || []).filter(c => c && !c.isPlusLocked);
      const lockedChats = (chats || []).filter(c => c && c.isPlusLocked);

      // If it's first page, clear existing search results
      if (page === 1) {
        set({ searchResults: [], searchLockedResults: [] });
      }

      // Process normal chats through addChats helper to ensure all required fields
      get().addChats(normalChats);

      // Get the processed chats from the store using their IDs
      const processedChats = normalChats
        .map(chat => get().chats.find(c => c.id === chat.id))
        .filter(Boolean);

      // Update search results with the processed chats. Locked
      // placeholders are stored in `searchLockedResults` so the sidebar
      // can render them as opaque "[Plus-only result]" rows without
      // polluting the chat list (which downstream code assumes is a
      // homogeneous array of fully-shaped chats).
      set(state => ({
        searchResults: [...state.searchResults, ...processedChats],
        searchLockedResults: [...state.searchLockedResults, ...lockedChats],
        searchPagination: pagination,
        ...(page === 1 ? { searchPlusLock: plusLock || null } : {}),
      }));

      return processedChats;
    } catch (error) {
      set({ searchError: error.message });
      throw error;
    } finally {
      set({ isSearching: false });
    }
  },

  clearSearch: () => {
    set({
      searchQuery: '',
      searchResults: [],
      searchLockedResults: [],
      searchPlusLock: null,
      searchPagination: {
        current: 1,
        pages: 1,
        total: 0,
        perPage: 20,
        hasMore: false
      },
      searchError: null
    });
  },

  // Deep search actions (message content search)
  deepSearchMessages: async (query, { page = 1, conversationIds = null } = {}) => {
    if (!query.trim()) {
      set(state => ({
        deepSearch: {
          ...state.deepSearch,
          query: '',
          results: [],
          mode: 'global',
          pagination: { current: 1, pages: 1, total: 0, perPage: 20, hasMore: false },
          error: null,
        }
      }));
      return;
    }

    const isScoped = Array.isArray(conversationIds) && conversationIds.length > 0;

    try {
      const prev = get().deepSearch;
      set({
        deepSearch: {
          ...prev,
          query,
          isSearching: true,
          error: null,
          ...(page === 1 ? { results: [] } : {}),
        }
      });

      const response = await chatsApi.searchMessages(query, { page, conversationIds });

      const current = get().deepSearch;

      const incoming = Array.isArray(response.results) ? response.results : [];
      const mergedResults = page === 1 ? incoming : [...current.results, ...incoming];

      set({
        deepSearch: {
          ...current,
          results: mergedResults,
          mode: response.mode,
          pagination: response.pagination,
          isSearching: false,
        }
      });

      return incoming;
    } catch (error) {
      set({
        deepSearch: { ...get().deepSearch, error: error.message, isSearching: false }
      });
    }
  },

  setDeepSearchScope: (conversationIds, conversationTitles = {}) => {
    set(state => ({
      deepSearch: {
        ...state.deepSearch,
        scopedConversationIds: conversationIds,
        scopedConversationTitles: conversationTitles,
        results: [],
        pagination: { current: 1, pages: 1, total: 0, perPage: 20, hasMore: false },
      }
    }));
  },

  clearDeepSearchScope: () => {
    set(state => ({
      deepSearch: {
        ...state.deepSearch,
        scopedConversationIds: [],
        scopedConversationTitles: {},
        results: [],
        pagination: { current: 1, pages: 1, total: 0, perPage: 20, hasMore: false },
      }
    }));
  },

  clearDeepSearch: () => {
    set(state => ({
      deepSearch: {
        query: '',
        results: [],
        mode: 'global',
        scopedConversationIds: [],
        scopedConversationTitles: {},
        pagination: { current: 1, pages: 1, total: 0, perPage: 20, hasMore: false },
        isSearching: false,
        error: null,
      }
    }));
  },

  clearChats: () => {
    set({
      chats: [],
      filters: {
        type: 'all',
        sort: null,
        inCollectionId: null,
        withoutCollections: false
      },
      searchQuery: '',
      searchResults: [],
      searchLockedResults: [],
      plusLock: null,
      searchPlusLock: null,
      selectedCitation: null,
      followUpSuggestions: {},
      collectionSuggestions: {},
      searchPagination: {
        current: 1,
        pages: 1,
        total: 0,
        perPage: 20,
        hasMore: false,
        filter: null,
        sort: null,
        inCollectionId: null,
        withoutCollections: false
      },
      isSearching: false,
      searchError: null,
      deepSearch: {
        query: '',
        results: [],
        mode: 'global',
        scopedConversationIds: [],
        scopedConversationTitles: {},
        pagination: { current: 1, pages: 1, total: 0, perPage: 20, hasMore: false },
        isSearching: false,
        error: null,
      },
      pagination: {
        current: 1,
        pages: 1,
        total: 0,
        perPage: 20,
        hasMore: false,
        filter: null,
        sort: null,
        inCollectionId: null,
        withoutCollections: false
      }
    });
  },

  // Add new action for setting selected citation
  setSelectedCitation: (conversationId, messageId, citationIndex) => {
    // If any parameter is null/undefined, reset the selection
    if (!conversationId || !messageId || citationIndex == null) {
      set(state => {
        // Only update if there's actually a change
        if (state.selectedCitation !== null) {
          return { selectedCitation: null };
        }
        return state;
      });
      return;
    }

    // Create the new citation identifier
    const newCitation = `${conversationId}.${messageId}.${citationIndex}`;

    // Update only if different from current selection
    set(state => {
      if (state.selectedCitation !== newCitation) {
        return { selectedCitation: newCitation };
      }
      return state;
    });
  },
}),
  {
    name: 'chat-storage',
    storage: createJSONStorage(() => getStorage()),
    partialize: (state) => ({
      // Temporary chats must never hit disk. They're cleaned up on
      // navigate-away from c/[id] (see useEffect there + the backend
      // delete-temporary-conversation endpoint), but if the user kills
      // the app from inside a temp chat the cleanup never runs and the
      // chat gets rehydrated into the sidebar on next launch. Stripping
      // them at partialize time guarantees a temp chat can never survive
      // an app restart, regardless of whether the cleanup fired.
      chats: state.chats
        .filter(chat => chat?.flags?.isTemporary !== true)
        .map(chat => ({
          id: chat.id,
          title: chat.title,
          href: chat.href,
          inCollectionId: chat.inCollectionId,
          coworkerSession: chat.coworkerSession || null,
          isArchived: chat.isArchived,
          isPinned: chat.isPinned,
          visibility: chat.visibility,
          lastMessageTime: chat.lastMessageTime,
          unreadCount: chat.unreadCount,
          isOwner: chat.isOwner,
          createdAt: chat.createdAt,
          updatedAt: chat.updatedAt,
          flags: chat.flags,
          stats: chat.stats,
          messages: [],
          isInferencing: false,
          isInferencingWaiting: false,
        })),
      pagination: state.pagination,
    }),
    onRehydrateStorage: () => (rehydrated) => queueMicrotask(() => {
      // Deferred via queueMicrotask: see getStores().useAuthStore.onRehydrateStorage —
      // sync web hydration runs this during create(), before `useChatStore`
      // is initialized (TDZ), and a throw here leaves persist hydration
      // permanently unfinished.
      //
      // CRASH GUARD: queueMicrotask runs OUTSIDE zustand's hydration promise
      // chain, so an uncaught throw here is a fatal unhandled exception in
      // release builds (it hard-crashed iOS on launch on 2026-06-11 via the
      // unguarded window.addEventListener call below). Never let this body
      // throw.
      try {
      // Purge three classes of stale chats that the persisted store could
      // be holding from older app versions:
      //
      // 1. Hidden tool-run backing conversations (`flags.isToolRun: true`,
      //    not yet promoted to chat). The backend filter excludes these,
      //    `addChats` filters incoming, but localStorage rehydrates around
      //    those gates — so the same rows would otherwise reappear forever.
      //
      // 2. "Voice Conversation" placeholder rows fabricated by `addMessage`
      //    when a `new_assistant_message` socket event arrived for an
      //    unknown conversationId. These were created with `flags: {}` and
      //    title "Voice Conversation", and they leak the title of every
      //    hidden tool-run conversation into the sidebar (see addMessage
      //    comment for full backstory). The placeholder is unambiguously
      //    detectable by `flags` having zero keys — every chat that comes
      //    through the proper `addChats` path has at least `isFlagged`,
      //    `isTemporary`, `fastMode` defined. Anything with `flags: {}` in
      //    persisted state is a placeholder.
      //
      // 3. Temporary chats. New `partialize` strips these out so they
      //    never get written, but installs that already have a temp chat
      //    persisted from a prior app version need this catch-up purge.
      const cached = rehydrated?.chats;
      if (Array.isArray(cached) && cached.length > 0) {
        const cleaned = cached.filter(c => {
          if (isHiddenToolRunChat(c)) return false;
          if (c?.flags?.isTemporary === true) return false;
          const flagKeyCount = c?.flags ? Object.keys(c.flags).length : 0;
          if (flagKeyCount === 0) return false; // fabricated placeholder
          return true;
        });
        if (cleaned.length !== cached.length) {
          useChatStore.setState({ chats: cleaned });
        }
      }

      useChatStore.setState({ _hasHydrated: true });

      // Persisted rows can include chats deleted on another device while
      // this client was closed. Kick an existence check once the cache is
      // back in memory (auth token may still be hydrating; the API client
      // waits for it).
      try {
        if (useChatStore.getState().chats?.length) {
          useChatStore.getState().reconcileDeletedChats();
        }
      } catch (_) { /* best-effort */ }

      // Listen for cross-tab localStorage writes so other tabs'
      // newly created conversations appear without a refresh.
      // Web only: on native, `window` is the RN global and has no
      // addEventListener — calling it throws.
      if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('storage', (e) => {
          if (e.key !== 'chat-storage' || !e.newValue) return;
          try {
            const parsed = JSON.parse(e.newValue);
            const remoteChats = parsed?.state?.chats;
            if (!Array.isArray(remoteChats) || remoteChats.length === 0) return;

            const localIds = new Set(useChatStore.getState().chats.map(c => c.id));
            const newChats = remoteChats.filter(c => !localIds.has(c.id));
            if (newChats.length > 0) {
              // addChats already drops hidden tool-run rows internally.
              useChatStore.getState().addChats(newChats);
            }
          } catch (_) { /* best-effort */ }
        });
      }
      } catch (err) {
        console.error('[Store] Chat store rehydrate post-processing failed:', err);
      }
    }),
  }
));

export { useChatStore };
export default useChatStore;
